// This file has been generated by Specta. Do not edit this file manually.

/**  A term the room explains on hover. An uncased term matches any casing. */
export type AbbreviationView = {
	term: string,
	definition: string,
	cased: boolean,
};

export type AnimalIdentityView = {
	is_animal: string | null,
	has_animal: string | null,
	animal_need: string | null,
};

export type ArgumentDraft = boolean | string[] | string;

export type ArgumentError = "required" | "invalid";

export type ArgumentValue = LiteralValue[] | LiteralValue;

export type AttachmentInfoView = {
	width: number | null,
	height: number | null,
	duration_ms: number | null,
	animated: boolean | null,
	blurhash: string | null,
	waveform: number[] | null,
	voice: boolean,
	audio_metadata: AudioMetadataView | null,
};

/**  MSC4549 track details from an `m.audio` event's `info`. */
export type AudioMetadataView = {
	title: string | null,
	artist: string | null,
	album: string | null,
	cover_art_blurhash: string | null,
};

export type AuthIntent = "login" | "register";

export type AvatarChangeView = {
	old: string | null,
	new: string | null,
};

export type AvatarEditView = { kind: "keep" } | { kind: "clear" } | { kind: "url"; url: string } |
/**  Encrypted in the webview with the account data key in hand. */
{ kind: "encrypted"; file: unknown };

export type BookmarkView = {
	bookmark_id: string,
	room_id: string,
	event_id: string,
	room_name: string | null,
	sender: string | null,
	body_preview: string | null,
	event_ts: number,
	bookmarked_ts: number,
};

export type BotCommand = {
	sender: string,
	sender_name: string | null,
	sender_avatar: string | null,
	command: string,
	aliases: string[],
	description: string | null,
	parameters: BotCommandParameter[],
	/**  Takes the rest of the line, spaces and all. */
	tail_parameter: string | null,
};

/**  An `org.matrix.msc4391.command_description` and who it came from. */
export type BotCommandDescription = {
	sender: string,
	sender_name: string | null,
	sender_avatar: string | null,
	content: unknown,
};

/**
 *  The `org.matrix.msc4391.command` block; an argument leaves out whatever it
 *  does not set, as bots compare these objects field by field.
 */
export type BotCommandInvocation = {
	command: string,
	arguments: { [key in string]: unknown },
};

export type BotCommandMatch = {
	command: BotCommand,
	args: string,
	raw_args: string,
};

export type BotCommandParameter = {
	key: string,
	schema: ParameterSchema,
	description: string | null,
	optional: boolean,
	default_value: ArgumentValue | null,
	/**  `moe.sable.flag`: the option name the bot's own parser expects. */
	flag: string | null,
};

export type BrightnessView = "light" | "dark";

/**  One `JSCalendar` event from a `chat.commet.calendar_events` room event. */
export type CalendarEntryView = {
	event_id: string,
	sender: string,
	timestamp: number,
	event: unknown,
};

export type CalendarItem = {
	event_id: string,
	sender: string,
	uid: string,
	title: string,
	description: string,
	location: string,
	all_day: boolean,
	/**  Wall-clock `YYYY-MM-DDTHH:MM[:SS]` in `time_zone`, or floating if none. */
	start: string,
	time_zone: string | null,
	duration_ms: number,
	recurrence: Recurrence | null,
	raw: { [key in string]: unknown },
};

export type CalendarRsvpView = {
	sender: string,
	calendar_event_id: string,
	uid: string,
	recurrence_id: string | null,
	status: string,
	timestamp: number,
};

export type CalendarView = {
	entries: CalendarEntryView[],
	rsvps: CalendarRsvpView[],
};

export type CallBackendView = {
	id: string,
	url: string,
	jwt: string,
	identity: string,
};

export type CallCompatibilityView = {
	sticky_events: boolean,
	delayed_events: boolean,
	rtc_transports: RtcTransportSource,
};

export type CallIntent = "audio" | "video";

export type CallMemberView = {
	user_id: string,
	device_id: string,
	identity: string,
	backend_id: string | null,
	joined_ts: number,
	screen_sharing: boolean,
};

export type CallMode = "legacy" | "compatibility" | "matrix_2";

export type CallModeReason = "requested" | "sticky_only" | "legacy_peer" | "sticky_peer" | "fresh";

export type CallSessionId = number;

export type CallSignalingStage = "membership" | "sync" | "provision";

export type CallSupportView = {
	has_focus: boolean,
	can_join: boolean,
};

export type ChildOrderOverride = {
	parent_id: string,
	children: SpaceChildEdge[],
};

export type ComposerContent = {
	body: string,
	formatted_body: string | null,
	mentions: ComposerMentions,
};

export type ComposerMentions = {
	user_ids: string[],
	room: boolean,
};

/**  A mention or emote picked from a suggestion rather than typed. */
export type ComposerPill = { kind: "mention"; user_id: string; name: string; via: string[] } | { kind: "emote"; shortcode: string; url: string; body: string | null } | { kind: "image"; src: string; alt: string; title: string };

/**
 *  Held in a shown notification's data and read back by the next push for the
 *  same room, so it keeps the field names notifications already carry.
 */
export type ConversationLine = {
	sender: string | null,
	body: string,
	eventId: string | null,
};

/**  Pushed, unsolicited. Never a reply to a command. */
export type CoreEvent = ({ type: "account_lock_changed"; account_id: string; locked: boolean }) & { avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | {
	type: "sync_status",
} & SyncStatus | ({ type: "session_ended"; reason: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "session_tokens_refreshed" }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } |
/**  One batch is one render, so it stays batched all the way to the UI. */
({ type: "room_list_diff"; subscription: SubscriptionId; diffs: VectorDiff<RoomListItemView>[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "timeline_diff"; subscription: SubscriptionId; diffs: VectorDiff<TimelineItemView>[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "threads_changed"; room_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "timeline_pagination"; subscription: SubscriptionId; loading: boolean; reached_start: boolean }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "timeline_aggregations"; subscription: SubscriptionId; items: TimelineItemView[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; total?: never; user_id?: never; user_ids?: never } |
/**
 *  Our own user excluded. Absolute, so an empty list replaces the previous
 *  one. Sent for every joined room, since a room list row shows it too.
 */
({ type: "typing"; room_id: string; user_ids: string[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never } |
/**  Pushed on every change, so the UI never polls to notice it is verified. */
({ type: "encryption_status"; status: EncryptionStatusView }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "key_backup_download"; download: KeyBackupDownloadView }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "devices_changed"; devices: DeviceView[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } |
/**  Fetched once per session start. `None` when the homeserver has no OAuth. */
({ type: "oauth_metadata"; metadata: OAuthMetadataView | null }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "search_coverage"; coverage: SearchCoverageView }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "notification"; notification: NotificationView }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "notification_settings_changed" }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "inbox_changed" }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "account_data_changed"; event_type: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "personas_changed" }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "space_sidebar_changed"; items: SidebarItemView[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } |
/**  A calendar entry, an answer or a redaction arrived in a calendar room. */
({ type: "room_widgets_changed"; room_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "widget_room_event"; event: unknown }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "widget_to_device"; event: unknown; encrypted: boolean }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "calendar_changed"; room_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "room_cosmetics_changed"; room_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "profile_changed"; room_id: string; user_id: string; display_name: string | null; avatar_url: string | null }) & { account_id?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_ids?: never } |
/**  A member event of any kind arrived: join, leave, kick, ban, invite or a profile change. */
({ type: "members_changed"; room_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "bot_commands_changed"; room_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "image_packs_changed"; room_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } |
/**  An incoming request arrives unsolicited. There is no other prompt. */
({ type: "verification"; user_id: string; flow_id: string; state: VerificationView }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_ids?: never } | ({ type: "qr_login"; grant: boolean; progress: QrLoginProgressView }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "call_encryption_key"; session: CallSessionId; identity: string; key_index: number; key: string; own: boolean; backend_id: string | null }) & { account_id?: never; avatar_url?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; items?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "call_backends"; session: CallSessionId; revision: number; publisher_id: string; backends: CallBackendView[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; reached_start?: never; reason?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "call_signaling_error"; session: CallSessionId; stage: CallSignalingStage; fatal: boolean }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; source?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "call_members"; session: CallSessionId; members: CallMemberView[] }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "incoming_call"; room_id: string; notification_event_id: string; sender: string; sender_name: string | null; room_name: string | null; ring: boolean; has_video: boolean; expires_at_ms: number }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; fatal?: never; flow_id?: never; grant?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "incoming_call_ended"; notification_event_id: string }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; total?: never; user_id?: never; user_ids?: never } | ({ type: "media_progress"; source: string; current: number; total: number }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; last_active_ago?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; presence?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; stage?: never; state?: never; status?: never; status_message?: never; subscription?: never; user_id?: never; user_ids?: never } |
/**  Never arrives on a homeserver with presence disabled. */
({ type: "presence"; user_id: string; presence: PresenceView; status_message: string | null; last_active_ago: number | null }) & { account_id?: never; avatar_url?: never; backend_id?: never; backends?: never; coverage?: never; current?: never; devices?: never; diffs?: never; display_name?: never; download?: never; encrypted?: never; event?: never; event_type?: never; expires_at_ms?: never; fatal?: never; flow_id?: never; grant?: never; has_video?: never; identity?: never; items?: never; key?: never; key_index?: never; loading?: never; locked?: never; members?: never; metadata?: never; notification?: never; notification_event_id?: never; own?: never; progress?: never; publisher_id?: never; reached_start?: never; reason?: never; revision?: never; ring?: never; room_id?: never; room_name?: never; sender?: never; sender_name?: never; session?: never; source?: never; stage?: never; state?: never; status?: never; subscription?: never; total?: never; user_ids?: never };

export type CreateJoinRuleView = "public" | "invite" | "knock" | "restricted" | "knock_restricted";

export type DefaultNotificationModesView = {
	direct: NotificationModeView,
	group: NotificationModeView,
};

export type DeviceView = {
	device_id: string,
	display_name: string | null,
	is_verified: boolean,
	/**
	 *  Signed by the account's own identity, whether or not this device trusts
	 *  that identity yet. What a new device can be confirmed from.
	 */
	cross_signed: boolean,
	/**  The device has uploaded device keys, so it can be verified at all. */
	has_keys: boolean,
	/**  The session this core is running in. */
	is_own: boolean,
	last_seen_ts: number | null,
	last_seen_ip: string | null,
};

export type DiagnosticPushView = { kind: "no_pusher" } | { kind: "no_gateway" } | { kind: "refused" } | { kind: "rejected" } | { kind: "unforwarded" } | { kind: "sent"; event_id: string; accepted: boolean | null };

export type DirectoryRoomType = "rooms" | "spaces";

/**
 *  `None` on either side means the name was unset, which reads differently from
 *  a rename.
 */
export type DisplayNameChangeView = {
	old: string | null,
	new: string | null,
};

export type DmSpaceLink = {
	room_id: string,
	space_ids: string[],
};

export type DropInstruction = "above" | "below" | "into";

export type EditVersionView = {
	event_id: string,
	timestamp: number,
	body: string,
	html: string,
};

export type EmojiView = {
	symbol: string,
	/**  English, from the spec's table. */
	description: string,
};

export type EmoteCell = ({ emoji: string }) & { image?: never } | ({ image: PackImageView }) & { emoji?: never };

export type EmoteRecents = {
	images: PackImageView[],
	cells: EmoteCell[],
};

export type EmoteRecentsInput = {
	recent: string[],
	frequent: string[],
	images: PackImageView[],
	unicode: boolean,
	reactions: boolean,
};

export type EncryptionStatusView = {
	/**  Whether *this* device is signed by our own identity. */
	verification: VerificationStateView,
	recovery: RecoveryStateView,
	/**
	 *  All three keys held locally, so this device can sign others. False means
	 *  verification must come from another session.
	 */
	cross_signing_ready: boolean,
	signing_keys: SigningKeysView,
	backup_unlocked: boolean,
	/**  The default secret storage key can also be unlocked with a passphrase. */
	recovery_passphrase: boolean,
	account_data_key: boolean,
};

/**  What the event dialog edits, with its start already read as local wall time. */
export type EventDraft = {
	title: string,
	description: string,
	location: string,
	start: string,
	duration_ms: number,
	all_day: boolean,
	frequency: Frequency | null,
	/**  A date; the series ends after that day. */
	until: string | null,
};

export type EventNotificationView = "membership" | "reactions" | "edits" | "notices" | "invites" | "calls";

export type EventNotificationsView = {
	membership: boolean | null,
	reactions: boolean | null,
	edits: boolean | null,
	notices: boolean | null,
	invites: boolean | null,
	calls: boolean | null,
};

export type EventPreview = {
	sender: string | null,
	body: string | null,
	fallback: ReplyFallbackView | null,
};

/**
 *  One field of a state event a command rewrites. `Set` with an empty value
 *  removes the field, as `Clear` does.
 */
export type FieldEdit = { kind: "keep" } | { kind: "clear" } | { kind: "set"; value: string };

export type ForwardedView = {
	timestamp: number | null,
	room_id: string | null,
	event_id: string | null,
	sender: string | null,
	content_valid: boolean | null,
};

export type Frequency = "daily" | "weekly" | "monthly" | "yearly";

export type GalleryAttachmentView = {
	filename: string,
	mime: string,
	info: AttachmentInfoView | null,
	spoiler: boolean,
};

export type GalleryItemView = { kind: "image"; filename: string; caption: string | null; source: string; mime: string | null; width: number | null; height: number | null; size: number | null; blurhash: string | null; thumbnail: string | null; spoiler: string | null } | { kind: "video"; filename: string; caption: string | null; source: string; mime: string | null; width: number | null; height: number | null; blurhash: string | null; thumbnail: string | null; spoiler: string | null } | { kind: "audio"; filename: string; caption: string | null; source: string; mime: string | null; duration_ms: number | null; waveform: number[] | null } | { kind: "file"; filename: string; caption: string | null; source: string; mime: string | null; size: number | null };

export type GestureView = "hug" | "cuddle" | "wave" | "poke" | "headpat";

export type HierarchyRoom = {
	room: HierarchyRoomView,
	suggested: boolean,
	via: string[],
	/**  The path from the root, so a room listed under two spaces has two keys. */
	key: string,
	/**  What the reader can do to get in from this section, joined or not. */
	action: LobbyAction | null,
};

/**
 *  A room as the lobby knows it, from the server's hierarchy or the local room
 *  list; a local room has no member count.
 */
export type HierarchyRoomView = {
	room_id: string,
	canonical_alias: string | null,
	name: string | null,
	topic: string | null,
	avatar_url: string | null,
	is_space: boolean,
	num_joined_members: number | null,
	join_rule: RoomJoinRuleView,
	allowed_room_ids: string[],
	guest_can_join: boolean,
	children: SpaceChildEdge[],
};

export type HierarchySection = {
	/**  `None` for the root's own rooms. */
	space: HierarchyRoomView | null,
	suggested: boolean,
	depth: number,
	key: string,
	rooms: HierarchyRoom[],
	parent_id: string,
	owner_id: string | null,
	siblings: SpaceChildEdge[],
	loaded: boolean,
	failed: boolean,
	/**  Children named by an edge that the hierarchy has not described yet. */
	pending: number,
	/**  What the reader can do to get into `space` from its owner. */
	space_action: LobbyAction | null,
};

export type HistoryVisibilityView = "shared" | "invited" | "joined" | "world_readable";

export type HomeserverSoftwareView = {
	name: string | null,
	version: string | null,
};

export type IdentityResetStep = { step: "done"; recovery_key: string } | { step: "password" } | { step: "approve"; url: string };

export type ImagePackOriginView = "account" | "room" | "global" | "space";

export type ImagePackView = {
	/**
	 *  The state key for a room pack, empty for the account's own pack. Unique
	 *  only together with `room_id`.
	 */
	id: string,
	origin: ImagePackOriginView,
	room_id: string | null,
	name: string | null,
	avatar_url: string | null,
	declared_name: string | null,
	declared_avatar_url: string | null,
	attribution: string | null,
	usage: ImageUsageView[],
	images: PackImageView[],
	stable_event: boolean,
	legacy_event: boolean,
};

export type ImageSourcePackReferenceView = {
	url: string,
	source: ImageSourcePackView,
};

export type ImageSourcePackView = {
	room_id: string,
	state_key: string,
	shortcode: string,
	via: string[],
};

export type ImageUsageView = "emoticon" | "sticker";

export type InboxFilter = "all" | "mentions" | "direct" | "threads";

export type InboxItemView = {
	room_id: string,
	event_id: string,
	ts: number,
	sender: string,
	sender_name: string | null,
	body: string | null,
	highlight: boolean,
	is_direct: boolean,
	encrypted: boolean,
	read: boolean,
	thread_root: string | null,
};

export type InviteAvatarsView = "on" | "off";

export type InviteTriageView = {
	room_id: string,
	inviter: string | null,
	reason: string | null,
	shares_room: boolean,
	inviter_banned: boolean,
};

export type InvocationResult = { kind: "built"; invocation: BotCommandInvocation; body: string } | { kind: "invalid"; errors: { [key in string]: ArgumentError } };

export type ItemSchema = { schema_type: "primitive"; type: PrimitiveType } | { schema_type: "literal"; value: LiteralValue } | { schema_type: "union"; variants: ScalarSchema[] };

export type JoinAddress = {
	address: string,
	via: string[],
};

export type JoinCandidate = {
	room: HierarchyRoomView,
	via: string[],
	parent_id: string,
};

/**
 *  `restricted` and `knock_restricted` need an allowed-spaces list, so they are
 *  not settable here.
 */
export type JoinRuleView = "public" | "invite" |
/**  Anyone may ask. A member approves. */
"knock" | "restricted" | "knock_restricted";

export type KeyBackupDownloadState = "downloading" | "importing" | "complete" | "failed";

export type KeyBackupDownloadView = {
	account_id: string,
	request_id: string,
	state: KeyBackupDownloadState,
	total: number | null,
	processed: number,
	imported: number,
	failed: number,
};

export type KeyBackupStatusView = {
	local_keys: number,
	backed_up_keys: number,
	cloud_keys: number | null,
	can_restore: boolean,
	download: KeyBackupDownloadView | null,
};

export type KeywordNotificationView = {
	keyword: string,
	mode: MentionNotificationModeView,
};

/**  Structured, so arranging and localising the preview stays with the UI. */
export type LatestEventView = {
	sender: string | null,
	/**  Plain text: a list row must not run untrusted HTML. */
	body: string,
	timestamp: number | null,
	sending: boolean,
	event_id: string | null,
};

/**
 *  What a drag picked up or landed on: a space, possibly inside a folder, or a
 *  whole folder.
 */
export type LayoutRef = { kind: "space"; room_id: string; folder_id: string | null } | { kind: "folder"; folder_id: string };

export type LiteralValue = boolean | number | string | RoomReference;

export type LobbyAction = "join" | "knock";

export type LoginFlowsView = {
	password: boolean,
	oidc: boolean,
	oidc_registration: boolean,
	qr_login: boolean,
	sso: boolean,
	oauth_aware_preferred: boolean,
	sso_identity_providers: SsoIdentityProviderView[],
};

export type LoginIdentifier = { kind: "user"; user: string } | { kind: "email"; address: string };

export type MatrixLink = { kind: "user"; user_id: string } | { kind: "room"; room_id: string } | { kind: "event"; room_id: string; event_id: string };

/**  MSC4278. `None` means the event does not say. */
export type MediaPreviewConfigView = {
	media_previews: MediaPreviewsView | null,
	invite_avatars: InviteAvatarsView | null,
};

export type MediaPreviewsView = "on" |
/**  Only in rooms that are not public. */
"private" | "off";

export type MemberLookEdit = {
	display_name: FieldEdit,
	avatar_url: FieldEdit,
	color_on_light: FieldEdit,
	color_on_dark: FieldEdit,
};

export type MemberLookView = {
	display_name: string | null,
	avatar_url: string | null,
	color_on_light: string | null,
	color_on_dark: string | null,
	pronouns: PronounView[],
};

export type MemberView = {
	user_id: string,
	display_name: string | null,
	avatar_url: string | null,
	power_level: number,
	membership: MembershipView,
	member_ts: number | null,
	kicked: boolean,
	service: boolean,
};

/**
 *  The SDK's `MembershipChange`, narrowed to the transitions worth wording.
 *  Anything unrecognised collapses to `Other`, which the UI hides.
 */
export type MembershipChangeView = "joined" | "left" | "banned" | "unbanned" | "kicked" | "invited" | "kicked_and_banned" | "invitation_accepted" | "invitation_rejected" | "invitation_revoked" | "knocked" | "knock_accepted" | "knock_retracted" | "knock_denied" | "other";

export type MembershipView = "join" | "invite" | "knock" | "leave" | "ban";

export type MentionNotificationModeView = "off" | "notify" | "loud";

export type MentionNotificationsView = {
	room: MentionNotificationModeView | null,
	user: MentionNotificationModeView | null,
	display_name: MentionNotificationModeView | null,
	username: MentionNotificationModeView | null,
};

export type MentionRuleView = "room" | "user" | "display_name" | "username";

/**  `Loud` covers `@room` and anything the push rules chose to highlight. */
export type MentionView = "none" | "silent" | "loud";

export type MessageKind = "text" | "emote" | "notice";

export type MessageShieldCode = "authenticity_not_guaranteed" | "unknown_device" | "unsigned_device" | "unverified_identity" | "verification_violation" | "mismatched_sender" | "sent_in_clear";

export type MessageShieldView = {
	code: MessageShieldCode,
	critical: boolean,
};

export type MfmColor = {
	raw: string,
	args: MfmColorArgs,
	text: string,
	/**  Where `text` starts in `raw`. */
	offset: number,
};

export type MfmColorArgs = {
	fg: string | null,
	bg: string | null,
};

export type MfmUnixtime = {
	raw: string,
	datetime: string,
};

export type MutualRoomView = {
	room_id: string,
	name: string | null,
	is_space: boolean,
};

export type NDay = {
	/**  0 for Sunday. */
	day: number,
	nth: number | null,
};

export type NotificationModeView = "all" | "mentions" | "mute";

export type NotificationSettingsView = {
	/**  The room's own rule. `null` means it follows `default`. */
	room: NotificationModeView | null,
	default: NotificationModeView,
};

export type NotificationView = {
	user_id: string,
	room_id: string,
	event_id: string | null,
	room_name: string,
	room_avatar_url: string | null,
	is_direct: boolean,
	encrypted: boolean,
	sender: string,
	sender_name: string | null,
	sender_avatar_url: string | null,
	body: string,
	mention: boolean,
	noisy: boolean | null,
};

/**  The account management part of the homeserver's OAuth 2.0 metadata (MSC4191). */
export type OAuthMetadataView = {
	account_management_uri: string | null,
	account_management_actions_supported: string[],
};

export type OpenIdTokenView = {
	access_token: string,
	token_type: string,
	matrix_server_name: string,
	expires_in_ms: number,
};

export type Outgoing = {
	thread_root: string | null,
	in_reply_to: string | null,
	silent_reply: boolean,
	mentions: string[],
	mentions_room: boolean,
	persona: PerMessageProfileView | null,
};

export type OverrideColorView = { kind: "unset" } | { kind: "cleared" } | { kind: "set"; light: string | null; dark: string | null };

/**
 *  MSC4529 keeps three states apart: no override, an override that removes
 *  the value, and an override that replaces it.
 */
export type OverrideTextView = { kind: "unset" } | { kind: "cleared" } | { kind: "set"; value: string };

export type PackAddressView = {
	room_id: string,
	state_key: string,
};

/**  What the pack editor holds; the core turns it into MSC2545 content. */
export type PackDraftView = {
	name: string,
	avatar_url: string | null,
	attribution: string,
	usage: ImageUsageView[],
	images: PackImageDraftView[],
};

export type PackImageDraftView = {
	shortcode: string,
	url: string,
	body: string | null,
	usage: ImageUsageView[],
	info: PackImageInfoView | null,
};

export type PackImageInfoView = {
	width: number | null,
	height: number | null,
	mimetype: string | null,
	size: number | null,
};

export type PackImageView = {
	shortcode: string,
	url: string,
	body: string | null,
	usage: ImageUsageView[],
	info: PackImageInfoView | null,
	source_pack: ImageSourcePackView | null,
};

export type PackTargetView = { kind: "room"; room_id: string; state_key: string } |
/**  `im.ponies.user_emotes`, the account's own pack. */
{ kind: "account" };

export type PaginationDirection = "backward" | "forward";

export type ParameterSchema = { schema_type: "primitive"; type: PrimitiveType } | { schema_type: "literal"; value: LiteralValue } | { schema_type: "union"; variants: ScalarSchema[] } | { schema_type: "array"; items: ItemSchema };

export type ParsedQuery = {
	text: string,
	phrases: string[],
	exclude: string[],
	tokens: SearchToken[],
	/**  Negated date bounds, which no filter can express. */
	unsupported: string[],
};

/**
 *  MSC4144 per-message profile, letting one account send under several
 *  identities. Read from the unstable `com.beeper.per_message_profile` key,
 *  falling back to the stable `m.per_message_profile` once servers emit it.
 */
export type PerMessageProfileView = {
	id: string | null,
	display_name: string | null,
	avatar_url: string | null,
	pronouns: PronounView[],
	/**
	 *  Author-chosen, so it is arbitrary and not theme-aware. The UI has to
	 *  hold it to a legibility floor against whatever surface is active.
	 */
	color_on_light: string | null,
	color_on_dark: string | null,
	/**
	 *  The sender prefixed the body with the profile name for clients that
	 *  cannot read the profile.
	 */
	has_fallback: boolean,
	/**  A (hopefully) short biography of this PMP (overrides extended profile) */
	biography: string | null,
	/**  A profile banner for this PMP (overrides extended profile) */
	banner_url: string | null,
	/**  Animal identity stuff (overrides extended profile) */
	is_animal: string | null,
	has_animal: string | null,
	animal_need: string | null,
};

export type PermissionGroup = {
	/**  A translation key. */
	label: string,
	items: PermissionItem[],
};

export type PermissionItem = {
	/**  A translation key. */
	label: string,
	location: PowerLevelLocation,
};

export type PersonaAlternateAvatarView = {
	id: string,
	avatar_url: string,
};

export type PersonaCatalogView = {
	personas: PersonaView[],
	account: PersonaSelectionView | null,
	rooms: { [key in string]: PersonaSelectionView },
	disabled_rooms: string[],
};

export type PersonaSelectionView = {
	persona_id: string,
	alternate_avatar_id: string | null,
	valid_until: number | null,
};

export type PersonaTriggerView = {
	prefix: string | null,
	suffix: string | null,
	keep_trigger: boolean,
	alternate_avatar_id: string | null,
};

export type PersonaView = {
	id: string,
	display_name: string,
	avatar_url: string | null,
	pronouns: PronounView[],
	color_on_light: string | null,
	color_on_dark: string | null,
	triggers: PersonaTriggerView[],
	pluralkit: PluralkitImportView | null,
	last_used: number | null,
	alternate_avatars: PersonaAlternateAvatarView[] | null,
	biography: string | null,
	banner_url: string | null,
	is_animal: string | null,
	has_animal: string | null,
	animal_need: string | null,
};

export type PinReadStateView = {
	pinned_ids: string[],
	unread: number,
	/**  Pins added since this account last looked. */
	fresh_ids: string[],
};

export type PluralkitImportView = {
	id: string,
	uuid: string | null,
	avatar_url: string | null,
	description: string | null,
};

export type PollAnswerView = {
	voters: string[] | null,
	id: string,
	text: string,
	/**  Absent while an undisclosed poll is still open. */
	votes: number | null,
	selected: boolean,
};

export type PollView = {
	question: string,
	answers: PollAnswerView[],
	max_selections: number,
	/**  Every answer's `votes` stays absent until `ended_at` is set. */
	undisclosed: boolean,
	/**  Votes cast after this are not counted. */
	ended_at: number | null,
	edited: boolean,
};

export type PowerLevelAction = "invite" | "kick" | "ban" | "redact";

/**
 *  Where one permission lives in `m.room.power_levels`. `Event` and `State`
 *  share the `events` map and differ only in the default they fall back to.
 */
export type PowerLevelLocation = { kind: "event"; event_type: string } | { kind: "state"; event_type: string } | { kind: "events_default" } | { kind: "state_default" } | { kind: "users_default" } | { kind: "action"; action: PowerLevelAction } | { kind: "notification_room" };

/**  A role name for a power level, from `in.cinny.room.power_level_tags`. */
export type PowerLevelTagView = {
	name: string,
	color: string | null,
	/**  A Unicode emoji or an `mxc://` URI. */
	icon: string | null,
};

export type PredecessorRoomView = {
	room_id: string,
	via: string[],
};

export type PresenceView = "online" | "offline" | "unavailable";

export type PrimitiveType = "string" | "integer" | "boolean" | "user_id" | "server_name" | "room_alias" | "room_id" | "event_id";

export type ProfileFieldView = {
	key: string,
	value: string,
};

export type ProfileOverrideView = {
	user_id: string,
	display_name: OverrideTextView,
	/**  An `mxc://` URI, or an encrypted file's JSON that `fetch_media` opens. */
	avatar: OverrideTextView,
	color: OverrideColorView,
};

export type ProfileOverridesView = {
	overrides: ProfileOverrideView[],
	protection: SealStateView,
	can_seal: boolean,
};

export type ProfilePropagationView = "all" | "unchanged" | "none";

export type ProfileView = {
	user_id: string,
	display_name: string | null,
	avatar_url: string | null,
	/**  Sanitised display HTML, safe to inject as-is. */
	bio: string | null,
	hero_color: string | null,
	/**
	 *  Whether the writer meant the hero colour to be read as a light or a dark
	 *  surface. Absent means the UI has to decide for itself.
	 */
	hero_brightness: BrightnessView | null,
	banner_url: string | null,
	status: StatusView | null,
	pronouns: PronounView[],
	/**  IANA zone name; the UI turns it into the member's local time. */
	timezone: string | null,
	/**
	 *  Already resolved against the deprecated per-theme fields, so the UI only
	 *  has to pick by the theme it is rendering.
	 */
	name_color_light: string | null,
	name_color_dark: string | null,
	animal: AnimalIdentityView | null,
	/**
	 *  Extended fields this client has no rendering for, kept so a profile that
	 *  another client wrote is still readable here.
	 */
	extra: ProfileFieldView[],
	supporter_awards: string | null,
	legacy_fields: string[],
};

export type PronounView = {
	summary: string,
	/**  Absent when the writer did not tag the set with a language. */
	language: string | null,
};

export type PublicRoomView = {
	room_id: string,
	canonical_alias: string | null,
	name: string | null,
	topic: string | null,
	avatar_url: string | null,
	room_kind: RoomKind | null,
	num_joined_members: number,
	join_rule: RoomJoinRuleView,
	guest_can_join: boolean,
	world_readable: boolean,
};

export type PushAlert = {
	title: string,
	body: string,
	line: ConversationLine,
	tag: string,
	room_id: string,
	event_id: string | null,
	ring: boolean,
	invite: boolean,
};

export type PushContent = {
	body: string | null,
	membership: string | null,
	notification_type: string | null,
};

export type PushEventView = {
	type: string,
	content: unknown,
	sender: string,
	sender_display_name: string | null,
	room_name: string,
	room_avatar_url: string | null,
	encrypted: boolean,
	noisy: boolean,
};

export type PushFetchView = { kind: "event"; event: PushEventView } | { kind: "discard" } | { kind: "unavailable" };

/**
 *  With `event_id_only` a push holds a room, an event and counts, and nothing
 *  else, so none of this can be relied on.
 */
export type PushNotification = {
	user_id: string | null,
	room_id: string | null,
	event_id: string | null,
	room_name: string | null,
	sender_display_name: string | null,
	type: string | null,
	membership: string | null,
	content: PushContent,
	unread: number | null,
	icon: string | null,
	decrypted: boolean,
	noisy: boolean,
};

export type PusherView = {
	pushkey: string,
	app_id: string,
	/**  The gateway's `_matrix/push/v1/notify`. */
	url: string,
	device_display_name: string,
	/**
	 *  Web push delivery, as `UnifiedPush` and a browser use: `pushkey` is then
	 *  the `p256dh` and the gateway encrypts to these.
	 */
	web_push: WebPushKeys | null,
	event_id_only: boolean,
	/**  False replaces any pusher already holding this key. */
	append: boolean,
};

/**  A square of `width` rows, `modules` holding `1` for a dark module. */
export type QrCodeView = {
	width: number,
	modules: string,
};

export type QrLoginFailureView = "unsupported" | "expired" | "check_code" | "declined" | "no_recovery" | "device_in_use" | "other";

export type QrLoginProgressView = { stage: "starting" } | { stage: "show_code"; code: QrCodeView } | { stage: "enter_check_code" } | { stage: "show_check_code"; check_code: number } | { stage: "waiting_for_token"; user_code: string } | { stage: "waiting_for_auth"; verification_uri: string } | { stage: "syncing_secrets" } | { stage: "done" } | { stage: "signed_in"; user_id: string } | { stage: "failed"; reason: QrLoginFailureView };

export type ReactionGroup = {
	key: string,
	senders: string[],
};

export type ReactionShortcodeView = {
	key: string,
	shortcode: string,
};

/**
 *  `incomplete` means secret storage exists but this device lacks secrets from
 *  it, so recovery will fix decryption.
 */
export type RecoveryStateView = "unknown" | "enabled" | "disabled" | "incomplete";

export type Recurrence = {
	frequency: Frequency,
	interval: number,
	count: number | null,
	until: string | null,
	by_day: NDay[],
	by_month_day: number[],
	first_day_of_week: number,
	/**  False for a rule part this does not expand; only the start is shown. */
	modelled: boolean,
};

/**  Read raw: ruma's `Pusher` carries neither a custom kind nor `activated`. */
export type RegisteredPusherView = {
	pushkey: string,
	app_id: string,
	/**  `http`, `email`, the web push kind, or a server-defined kind. */
	kind: string | null,
	device_display_name: string | null,
	/**  Some pusher kinds validate through a handshake; absent elsewhere. */
	activated: boolean | null,
	gateway: string | null,
	endpoint: string | null,
};

export type RegistrationFlowsView = {
	uiaa: boolean,
	email: RegistrationRequirementView,
	registration_token: RegistrationRequirementView,
};

export type RegistrationRequirementView = "unavailable" | "optional" | "required";

export type RegistrationResultView = { state: "complete"; user_id: string } | { state: "fallback"; stage: string; fallback_url: string; completed: string[]; total_stages: number } | { state: "email"; email: string | null; submit_url: string | null; can_complete_out_of_band: boolean; verified: boolean; completed: string[]; total_stages: number };

export type RelationsView = {
	chunk: unknown[],
	next_batch: string | null,
	prev_batch: string | null,
};

/**  What a quoted event was, when it has no body to show. */
export type ReplyFallbackView = { kind: "redacted" } | { kind: "reaction"; key: string } | { kind: "redaction" } | { kind: "verification_request" } | { kind: "event"; event_type: string };

/**
 *  The SDK loads the body lazily, so it is absent for an event we have never
 *  seen until `FetchEventDetails` fills it.
 */
export type ReplyView = {
	event_id: string,
	sender: string | null,
	sender_mentioned: boolean,
	sender_name: string | null,
	body: string | null,
	/**  Set instead of `body` when the replied-to event has none to show. */
	fallback: ReplyFallbackView | null,
};

export type ResolvedQuery = {
	filter: SearchFilter,
	/**
	 *  Tokens naming nothing this client knows, which must stop the search
	 *  rather than silently widen it.
	 */
	unresolved: SearchToken[],
	/**  A space with no joined rooms in it. */
	matches_nothing: boolean,
};

export type RoomAttachmentContentView = { kind: "image"; filename: string; source: string; mime: string | null; width: number | null; height: number | null; blurhash: string | null; thumbnail: string | null; spoiler: string | null } | { kind: "video"; filename: string; source: string; mime: string | null; width: number | null; height: number | null; blurhash: string | null; thumbnail: string | null; spoiler: string | null } | { kind: "file"; filename: string; source: string; mime: string | null; size: number | null } | { kind: "link"; urls: string[]; body: string };

export type RoomAttachmentKind = "media" | "file" | "link";

export type RoomAttachmentView = {
	event_id: string,
	gallery_index: number | null,
	sender: string,
	timestamp: number,
	content: RoomAttachmentContentView,
};

export type RoomCosmeticsView = {
	space_id: string | null,
	users: SenderCosmeticsView[],
};

export type RoomJoinRuleView = "public" | "invite" | "knock" |
/**  Members of an allowed space may join. */
"restricted" | "knock_restricted" | "private" |
/**  Not loaded, or a rule this client has no name for. */
"unknown";

/**  Room types that Sable supports. */
export type RoomKind =
/**  No room type */
"text" |
/**  `m.space` */
"space" |
/**  `org.matrix.msc3417.call` */
"voice" |
/**  `pl.chrome.forum` */
"forum" |
/**  `chat.commet.calendar` */
"calendar";

export type RoomListItemView = {
	room_id: string,
	canonical_alias: string | null,
	name: string | null,
	topic: string | null,
	/**  The topic's `text/html` representation, sanitised. */
	topic_html: string | null,
	avatar_url: string | null,
	is_direct: boolean,
	direct_targets: string[],
	join_rule: RoomJoinRuleView,
	/**  Only the tags this client models; others are dropped. */
	tags: RoomTag[],
	state: RoomStateView,
	/**  `null` until the state event loads, which is not the same as `false`. */
	encrypted: boolean | null,
	is_tombstoned: boolean,
	room_kind: RoomKind | null,
	/**
	 *  Members in the room's call, oldest first and one entry per user however
	 *  many devices they joined with.
	 */
	call_participants: string[],
	screen_sharers: string[],
	supports_knock: boolean,
	supports_restricted: boolean,
	supports_knock_restricted: boolean,
	/**  Already sorted by `order`, then the child event's age. */
	space_children: SpaceChildEdge[],
	unread: number,
	notifying: number,
	highlight: number,
	marked_unread: boolean,
	notification: NotificationSettingsView,
	/**  The counts above under the room's mode. */
	counts: RoomUnreadView,
	latest_event: LatestEventView | null,
};

export type RoomOpenView = {
	permissions: RoomPermissionsView,
	power_level_tags: { [key in string]: PowerLevelTagView },
	widgets: RoomStateEventView[],
	pinned_event_ids: string[],
	predecessor: PredecessorRoomView | null,
};

/**  What this account may do in one room, resolved from `m.room.power_levels`. */
export type RoomPermissionsView = {
	own_power_level: number,
	can_post: boolean,
	can_react: boolean,
	/**  Redacting your own event, which needs the level to send `m.room.redaction`. */
	can_redact_own: boolean,
	/**  Redacting someone else's event. */
	can_redact_others: boolean,
	can_invite: boolean,
	can_kick: boolean,
	can_ban: boolean,
	/**
	 *  `m.room.name`, `m.room.topic` and `m.room.avatar` share one level in
	 *  practice, so they are reported together.
	 */
	can_change_settings: boolean,
	can_pin: boolean,
	can_change_join_rule: boolean,
	can_change_power_levels: boolean,
	/**  `m.space.child`. Meaningless outside a space. */
	can_manage_children: boolean,
};

export type RoomPowerLevelsView = {
	ban: number,
	kick: number,
	redact: number,
	invite: number,
	events_default: number,
	state_default: number,
	users_default: number,
	events: { [key in string]: number },
	users: { [key in string]: number },
	notifications_room: number,
};

export type RoomPreviewViaView = { type: "client" } | { type: "remote"; server: string } | { type: "unknown" };

/**  A room as the server describes it to someone who may not be in it. */
export type RoomPreviewView = {
	/**  Resolved, since the preview may have been asked for by alias. */
	room_id: string,
	canonical_alias: string | null,
	name: string | null,
	topic: string | null,
	/**  The topic's `text/html` representation, sanitised. */
	topic_html: string | null,
	avatar_url: string | null,
	room_kind: RoomKind | null,
	/**  `null` when the server did not say. */
	num_joined_members: number | null,
	join_rule: RoomJoinRuleView,
	/**  `null` when this account has no membership in the room. */
	state: RoomStateView | null,
	via: RoomPreviewViaView,
};

export type RoomReference = {
	type: RoomReferenceKind,
	id: string,
	via: string[] | null,
	event_id: string | null,
};

export type RoomReferenceKind = "room_id" | "event_id";

export type RoomStateEventView = {
	state_key: string,
	content: unknown,
};

export type RoomStateView = "joined" | "invited" | "knocked" | "left" | "banned";

export type RoomTag = "favourite";

export type RoomUnreadView = {
	unread: number,
	/**  What the push rules notified on, which is what the inbox counts. */
	notifying: number,
	highlight: number,
	/**  Set by hand, so it survives a mute. */
	marked: boolean,
};

export type RoomVersionView = {
	id: string,
	stable: boolean,
};

export type RoomVersionsView = {
	default: string,
	available: RoomVersionView[],
};

/**
 *  Stable serialized codes. Source chains, inputs and bodies stay native-side
 *  and are logged there, so nothing leaks across the wire. The last four words
 *  match the platform ports, so a core failure and a capability failure read
 *  alike.
 */
export type RpcError = { code: "not_logged_in" } | { code: "unknown_subscription" } | { code: "unknown_call" } | { code: "no_call_focus" } | { code: "invalid_pagination_direction" } | { code: "unknown_room" } | { code: "unknown_homeserver" } | { code: "unknown_local_echo" } |
/**
 *  `stages` are the `m.login.*` types offered. With `m.login.password` the
 *  command can be retried carrying one, otherwise the user must finish on
 *  the homeserver.
 */
{ code: "interactive_auth_required"; stages: string[] } | { code: "unknown_verification" } |
/**  A scanned code that is not a Matrix verification code. */
{ code: "invalid_verification_code" } | { code: "invalid_media" } | { code: "message_too_large" } |
/**  A poll needs a question and between 1 and 20 answers. */
{ code: "invalid_poll" } | { code: "invalid_location" } | { code: "invalid_key_export" } | { code: "encrypted_schedule_unsupported" } | { code: "delayed_events_unsupported" } | { code: "sliding_sync_unsupported" } | { code: "account_locked" } | { code: "account_suspended" } |
/**  MSC2545 allows `[a-zA-Z0-9-_]`, at most 100 bytes, each once per pack. */
{ code: "invalid_shortcode"; shortcode: string } |
/**  A file that is not a backup this app wrote. */
{ code: "invalid_backup" } |
/**  Not an `http` or `https` URL. */
{ code: "invalid_url" } |
/**  Matrix JSON integers must fit in ±(2^53 - 1). */
{ code: "integer_out_of_range" } |
/**  A search filter naming something that is not a room or user id. */
{ code: "invalid_search_filter" } |
/**
 *  A sealed document this device cannot open, so writing it would erase
 *  what the other devices stored.
 */
{ code: "document_locked" } |
/**  Static: safe to hide UI. */
{ code: "unsupported" } |
/**  Retryable: keep UI. */
{ code: "unavailable" } |
/**  The media's server keeps failing; asking again before this is pointless. */
{ code: "media_server_unavailable"; retry_after_ms: number } |
/**  Refused, recoverable by user action. */
{ code: "denied" } | { code: "banned"; reason: string | null } | { code: "rate_limited"; retry_after_ms: number | null } | { code: "registration_unavailable" } | { code: "username_taken" } | { code: "invalid_username" } | { code: "invalid_email" } | { code: "unknown_email" } | { code: "email_verification_failed" } | { code: "weak_password" } | { code: "registration_stage_failed"; stage: string } |
/**  The homeserver advertises OAuth endpoints that cannot be reached. */
{ code: "auth_provider_unreachable" } |
/**  Detail is in the core's log under `log_id`. */
{ code: "failed"; log_id: string };

export type RsvpPeople = {
	accepted: string[],
	tentative: string[],
	declined: string[],
};

export type RsvpStatusView = "accepted" | "tentative" | "declined";

export type RsvpTally = {
	accepted: number,
	tentative: number,
	declined: number,
	mine: RsvpStatusView | null,
	people: RsvpPeople,
};

export type RtcLivekitEndpoint = "get_token" | "delegate_delayed_leave";

export type RtcTransportSource = "endpoint" | "well_known" | "none";

export type ScalarSchema = { schema_type: "primitive"; type: PrimitiveType } | { schema_type: "literal"; value: LiteralValue };

export type ScheduledMessageView = {
	delay_id: string,
	room_id: string,
	body: string,
	formatted: string | null,
	delay_ms: number,
	delivery_ts: number | null,
};

export type SealStateView = "plain" | "sealed" | "locked";

export type SealedAccountDataView = {
	content: unknown | null,
	state: SealStateView,
	can_seal: boolean,
};

export type SearchAttachment = "image" | "video" | "audio" | "file" | "link" | "poll";

export type SearchContextView = {
	event_id: string,
	body: string,
	sender: string,
	origin_server_ts: number,
};

export type SearchCoverageState = "indexing" | "complete" | "partial" | "stopped";

export type SearchCoverageView = {
	documents: number,
	rooms_pending: number,
	rooms_failed: number,
	state: SearchCoverageState,
};

export type SearchCrawlPhase = "starting" | "crawling" | "trickling" | "yielding" | "paused" | "backing_off" | "idle" | "budget_spent" | "index_full";

export type SearchDirectory = {
	rooms: SearchRoom[],
	people: SearchPerson[],
};

export type SearchFilter = {
	rooms: string[],
	senders: string[],
	mentions: string[],
	has: SearchAttachment[],
	not_rooms: string[],
	not_senders: string[],
	not_mentions: string[],
	not_has: SearchAttachment[],
	after_ts: number | null,
	before_ts: number | null,
	phrases: string[],
	exclude: string[],
	pinned: boolean | null,
	in_thread: boolean | null,
	file_types: string[],
	not_file_types: string[],
	pattern: string | null,
	state_events: boolean | null,
};

export type SearchHitView = {
	room_id: string,
	event_id: string,
	body: string,
	sender: string,
	origin_server_ts: number,
	score: number,
	context_before: SearchContextView[],
	context_after: SearchContextView[],
};

export type SearchMetricsView = {
	phase: SearchCrawlPhase,
	documents: number,
	documents_loaded: number,
	memory_bytes: number,
	memory_budget: number,
	disk_bytes: number,
	disk_budget: number,
	rooms_joined: number,
	rooms_indexed: number,
	rooms_pending: number,
	rooms_exhausted: number,
	rooms_failed: number,
	rooms_blind: number,
	rooms_unreadable: number,
	events_crawled: number,
	event_budget: number,
	batches: number,
	pushbacks: number,
	last_request_ms: number | null,
	average_request_ms: number | null,
	running_ms: number | null,
};

export type SearchOperator = "in" | "space" | "from" | "mentions" | "has" | "before" | "after" | "during" | "on" | "with" | "is" | "pinned" | "regex";

export type SearchOrder = "rank" | "recent" | "oldest";

export type SearchPerson = {
	user_id: string,
	display_name: string,
};

export type SearchRoom = {
	room_id: string,
	name: string | null,
	canonical_alias: string | null,
	joined: boolean,
	is_tombstoned: boolean,
	is_space: boolean,
	is_direct: boolean,
	direct_targets: string[],
	space_children: SpaceChildEdge[],
};

/**
 *  `start` and `end` are UTF-16 offsets, so they index the JS string the
 *  query was typed into.
 */
export type SearchToken = {
	operator: SearchOperator,
	value: string,
	negated: boolean,
	start: number,
	end: number,
};

export type SearchTuning = {
	crawl_pause_ms: number,
	trickle_pause_ms: number,
	flush_interval_secs: number,
	batch: number,
	base_events: number,
	max_events: number,
};

/**
 *  What the sections depend on besides the rooms. `loaded: None` treats every
 *  level as described; `keep_empty` names subspaces shown even without rooms.
 */
export type SectionInputs = {
	loaded: string[] | null,
	failed: string[],
	keep_empty: string[],
	joined: string[],
	invited: string[],
};

export type SendAttachmentRequest = {
	room_id: string,
	filename: string,
	mime: string,
	caption?: string | null,
	formatted_caption?: string | null,
	info?: AttachmentInfoView | null,
	spoiler?: boolean,
} & Outgoing;

export type SendBlockView = { kind: "identity_changed"; user_ids: string[] } | { kind: "verify_this_device" };

export type SendGalleryRequest = {
	room_id: string,
	attachments: GalleryAttachmentView[],
	caption?: string | null,
	formatted_caption?: string | null,
} & Outgoing;

/**  Without this a failed send renders as an ordinary message. */
export type SendStateView = { status: "sending";
/**  Media uploads only. */
progress: UploadProgressView | null } | { status: "failed"; error: string;
/**
 *  A recoverable failure resumes by itself. An unrecoverable one is
 *  parked until `RetrySend` or `CancelSend`, so only it needs a prompt.
 */
recoverable: boolean; blocked: SendBlockView | null } |
/**  Accepted, still waiting to arrive through sync. */
{ status: "sent" };

export type SenderCosmeticsView = {
	user_id: string,
	color_on_light: string | null,
	color_on_dark: string | null,
	pronouns: PronounView[],
	space_display_name: string | null,
	space_avatar_url: string | null,
};

export type SessionInfo = {
	account_id: string,
	user_id: string,
	device_id: string,
	homeserver: string,
	needs_reauth: boolean,
};

export type ShareHistoryView = "shared" | "unencrypted" | "no_cross_signing" | "history_not_shared";

export type SidebarItemView = { kind: "space"; room_id: string } | { kind: "folder"; id: string; name: string | null; content: string[] };

export type SignOutSafetyView = {
	encryption: EncryptionStatusView,
	backup_enabled: boolean,
	backup_uploaded: boolean,
	has_encrypted_rooms: boolean,
};

/**  Which private cross-signing keys this device holds. */
export type SigningKeysView = {
	master: boolean,
	self_signing: boolean,
	user_signing: boolean,
};

/**  What a slash command asks for; targets that need the room or account are left to the host. */
export type SlashInvocation = { command: "bug_report" } | { command: "gif_search"; query: string } | { command: "gesture"; gesture: GestureView; target: string | null } | { command: "message"; body: string; msgtype: MessageKind; formatted: string | null; verbatim: boolean } |
/**  `formatted` is the composer's HTML for the text, still to be coloured. */
{ command: "rainbow"; body: string; msgtype: MessageKind; formatted: string | null } | { command: "html"; html: string } | { command: "join"; address: string; via: string[] } | { command: "knock"; address: string; via: string[]; reason: string | null } | { command: "leave" } | { command: "invite"; user_id: string } | { command: "share_history"; user_id: string } | { command: "kick"; targets: string[]; reason: string | null } | { command: "ban"; targets: string[]; reason: string | null } | { command: "unban"; user_id: string; reason: string | null } | { command: "display_name"; name: string } | { command: "member_look"; look: MemberLookEdit; in_parent_space: boolean } | { command: "pronouns"; pronouns: PronounView[]; in_parent_space: boolean } | { command: "room_name"; name: string } | { command: "topic"; topic: string } | { command: "disinvite"; user_ids: string[] } | { command: "ignore"; user_ids: string[] } | { command: "unignore"; user_ids: string[] } | { command: "start_dm"; user_ids: string[] } | { command: "convert_to_dm"; user_id: string | null } | { command: "convert_to_room" } | { command: "discard_session" } | { command: "acl"; allow: string[]; deny: string[]; remove_allow: string[]; remove_deny: string[] } | { command: "add_widget"; url: string; name: string } | { command: "location"; latitude: number; longitude: number } | { command: "share_my_location" } | { command: "add_persona"; id: string; name: string | null; avatar: string | null } | { command: "remove_persona"; id: string } | { command: "use_persona"; id: string; valid_until: number | null } | { command: "reset_persona" } | { command: "disable_room_personas" } | { command: "persona_proxy"; id: string; prefix: string | null; suffix: string | null } | { command: "raw_message"; content: { [key in string]: unknown } } | { command: "raw_event"; event_type: string; content: { [key in string]: unknown }; state_key: string | null } | { command: "raw_account_data"; event_type: string; content: { [key in string]: unknown } } | { command: "delete_account_data_key"; event_type: string; key: string } | { command: "bulk_redact"; targets: string[]; after_ts: number; event_types: string[]; reason: string | null };

export type SlashParse = { kind: "none" } | { kind: "literal"; body: string } | { kind: "unknown"; name: string } | { kind: "usage"; name: string; developer_only: boolean } | { kind: "command"; name: string; developer_only: boolean; invocation: SlashInvocation };

export type SpaceChildEdge = {
	room_id: string,
	via: string[],
	/**  `m.space.child.content.order`, unordered children sort last. */
	order: string | null,
	origin_server_ts: number,
	/**  The parent marked this child as worth surfacing first. */
	suggested: boolean,
};

/**
 *  One room in a space's hierarchy. The root space is included, so a caller can
 *  walk the tree from it.
 */
export type SpaceHierarchyRoomView = {
	room_id: string,
	canonical_alias: string | null,
	name: string | null,
	topic: string | null,
	avatar_url: string | null,
	room_kind: RoomKind | null,
	num_joined_members: number,
	join_rule: RoomJoinRuleView,
	/**  The rooms a `restricted` or `knock_restricted` join rule admits members of. */
	allowed_room_ids: string[],
	guest_can_join: boolean,
	/**
	 *  This room's own `m.space.child` edges, already sorted. Empty unless it is
	 *  a space.
	 */
	children: SpaceChildEdge[],
};

export type SpaceParentView = {
	room_id: string,
	via: string[],
};

export type SsoIdentityProviderView = {
	id: string,
	name: string,
	icon: string | null,
	brand: string | null,
};

export type StateChangeView = { kind: "room_name"; name: string | null; previous: string | null } | { kind: "room_topic"; topic: string | null } | { kind: "room_avatar"; removed: boolean } | { kind: "pinned_events"; added: string[]; removed: string[]; total: number } |
/**  MSC3401. An update that neither joins nor leaves carries no change. */
{ kind: "call_membership"; joined: boolean };

/**
 *  MSC4426 status. The emoji is optional here even though the MSC requires
 *  it, because the older single-string status fields carry no emoji.
 */
export type StatusView = {
	text: string,
	emoji: string | null,
};

export type SubscriptionId = number;

export type SuggestedOverride = {
	parent_id: string,
	room_id: string,
	suggested: boolean,
};

export type SyncStatus = { state: "offline" } | { state: "syncing" } | { state: "live" } | { state: "error"; message: string };

export type TargetSuggestion = {
	label: string,
	value: string,
};

export type TermsPolicyView = {
	url: string,
	name: string,
};

export type ThreadReadDiagnostics = {
	root_event_id: string,
} & TimelineReadDiagnostics;

export type ThreadSummaryView = {
	/**  Excludes the root, so zero if every reply was redacted. */
	num_replies: number,
	/**
	 *  The latest reply's id, so the UI can strip its per-message-profile
	 *  fallback like a reply preview. Absent for a local echo or an unloaded
	 *  event.
	 */
	latest_event_id: string | null,
	latest_body: string | null,
	/**  What the latest reply was when it has no body to show. */
	latest_fallback: ReplyFallbackView | null,
};

export type TimelineFocusView = { kind: "live" } | { kind: "event"; event_id: string } | { kind: "thread"; root_event_id: string };

export type TimelineItemContentView = { kind: "message";
/**  Plain text, for previews and notifications. */
body: string;
/**  Sanitised display HTML, safe to inject as-is. */
html: string;
/**  An emote reads as an action by the sender rather than speech. */
msgtype: MessageKind; edited: boolean;
/**
 *  Drawn larger, 1 to 4, when the message is nothing but emoji or
 *  inline emotes.
 */
jumbo: number | null } | { kind: "image"; filename: string; caption: string | null;
/**  Sanitised display HTML for a formatted caption, when present. */
html: string | null; source: string; external_url: string | null; mime: string | null; width: number | null; height: number | null; size: number | null; blurhash: string | null; thumbnail: string | null; spoiler: string | null; animated: boolean | null } | { kind: "video"; filename: string; caption: string | null;
/**  Sanitised display HTML for a formatted caption, when present. */
html: string | null; source: string; external_url: string | null; mime: string | null; width: number | null; height: number | null; blurhash: string | null; thumbnail: string | null; spoiler: string | null; playback: VideoPlaybackView | null } | { kind: "audio"; filename: string; caption: string | null;
/**  Sanitised display HTML for a formatted caption, when present. */
html: string | null; source: string; external_url: string | null; mime: string | null; duration_ms: number | null; waveform: number[] | null; voice: boolean; metadata: AudioMetadataView | null } | { kind: "file"; filename: string; caption: string | null;
/**  Sanitised display HTML for a formatted caption, when present. */
html: string | null; source: string; external_url: string | null; mime: string | null; size: number | null } | { kind: "sticker"; body: string; source: string; mime: string | null; width: number | null; height: number | null } |
/**
 *  The coordinates are absent for a `geo:` URI we cannot read; `geo_uri` is
 *  passed through as sent either way.
 */
{ kind: "location"; body: string; geo_uri: string; latitude: number | null; longitude: number | null } | { kind: "live_location"; body: string; latitude: number | null; longitude: number | null; live: boolean; expires_at: number; updated_at: number | null } | { kind: "call_invite" } | { kind: "verification_request" } |
/**  The `im.fluffychat.cute_event` msgtype, such as a hug or a wave. */
{ kind: "cute"; cute_type: string | null; body: string } | { kind: "malformed"; event_type: string } |
/**  MSC4274. */
{ kind: "gallery";
/**  The caption shared by the whole set. */
body: string;
/**  Sanitised display HTML, safe to inject as-is. */
html: string; items: GalleryItemView[] } |
/**  MSC3381, with the responses and the end event already folded in. */
{ kind: "poll"; poll: PollView } | { kind: "redacted"; reason: string | null; redacted_by: string | null } | { kind: "unable_to_decrypt"; reason: UtdCauseView; session_id: string | null } | { kind: "membership"; user_id: string; change: MembershipChangeView;
/**
 *  The member's name at the time, so the copy does not have to fall
 *  back to a raw user id.
 */
display_name: string | null; reason: string | null } |
/**
 *  A display name or avatar change on an already-joined member. Separate
 *  from `Membership` because clients hide these by default.
 */
{ kind: "profile_change"; user_id: string; display_name: DisplayNameChangeView | null;
/**
 *  `new` is `None` when the member cleared their avatar rather than
 *  replacing it, which is a different sentence.
 */
avatar: AvatarChangeView | null } |
/**
 *  Any other state event. Reported rather than dropped so the UI can decide
 *  what to render and what to keep behind a "show hidden events" setting.
 */
{ kind: "state_event";
/**  e.g. `m.room.topic`. */
event_type: string; state_key: string;
/**
 *  Raw content, for the developer-only peek. Absent if the event's
 *  JSON is no longer around.
 */
content: unknown; prev_content: unknown;
/**  `None` leaves the UI with only `event_type` to show. */
change: StateChangeView | null } |
/**
 *  A message-like event the SDK has no item for. Only ever reaches the UI
 *  when the timeline was built with hidden events on; the default filter
 *  drops these.
 */
{ kind: "hidden_event";
/**  e.g. `m.key.verification.start`. */
event_type: string;
/**
 *  Raw content, for the developer-only peek. Absent if the event's
 *  JSON is no longer around.
 */
content: unknown; redacts: string | null } | { kind: "date_divider"; timestamp: number } | { kind: "read_marker" } | { kind: "timeline_start" } |
/**  Unmodelled, kept as a stub so wire indices stay aligned with the SDK's. */
{ kind: "unsupported"; description: string };

export type TimelineItemView = {
	/**  Not an event id. Stable across a local echo becoming remote. */
	id: string,
	row: TimelineRowKind,
	/**  Content another room can receive as it is. */
	forwardable: boolean,
	/**
	 *  Whether the event can be redacted at all; the room's power levels
	 *  decide whether the reader may.
	 */
	redactable: boolean,
	visibility: TimelineVisibility,
	/**  Absent while the event is still a local echo. */
	event_id: string | null,
	/**  A local echo's only handle, since it has no event id yet. */
	transaction_id: string | null,
	send_state: SendStateView | null,
	/**  Dividers and markers have no sender. */
	sender: string | null,
	sender_name: string | null,
	sender_avatar: string | null,
	timestamp: number,
	content: TimelineItemContentView,
	in_reply_to: ReplyView | null,
	/**
	 *  Set on the root and on every reply, so a UI with no thread view can hide
	 *  the replies.
	 */
	thread_root: string | null,
	thread_summary: ThreadSummaryView | null,
	reactions: ReactionGroup[],
	is_own: boolean,
	/**  Already reduced by the SDK to one receipt per user. */
	read_by: string[],
	/**  Available receipt timestamps in milliseconds since the Unix epoch. */
	read_timestamps: { [key in string]: number },
	/**
	 *  MSC4144. When set, this is the identity to show as the sender; `sender`
	 *  stays the account that actually sent it and must remain reachable.
	 */
	per_message_profile: PerMessageProfileView | null,
	bundled_link_previews: UrlPreviewView[],
	link_previews_removed: boolean | null,
	mention: MentionView,
	forwarded: ForwardedView | null,
	forum_title: string | null,
	shield: MessageShieldView | null,
};

export type TimelineReadDiagnostics = {
	unread: number,
	notifying: number,
	highlight: number,
	receipt_event_id: string | null,
};

/**
 *  How a timeline row is drawn: a message carries a sender, a hover menu and
 *  reactions; an event is a one-line notice; an annotation is a divider.
 */
export type TimelineRowKind = "message" | "event" | "annotation";

/**  Which reader preference can hide a row; `always` rows are always shown. */
export type TimelineVisibility = "always" | "membership" |
/**  A membership event that changed nothing a reader would notice. */
"membership_other" | "profile_change" | "tombstone" |
/**  Shown only with hidden events on. */
"hidden";

export type TurnServerView = {
	username: string,
	password: string,
	uris: string[],
	ttl_ms: number,
};

export type UnreadDiagnosticsView = {
	main: TimelineReadDiagnostics,
	server_notifications: number,
	server_highlights: number,
	threads: ThreadReadDiagnostics[],
};

export type UploadProgressView = {
	index: number,
	current: number,
	total: number,
};

export type UrlPreviewVideoView = {
	source: string,
	mime: string | null,
	width: number | null,
	height: number | null,
};

export type UrlPreviewView = {
	url: string,
	title: string | null,
	description: string | null,
	site_name: string | null,
	image: string | null,
	image_mime: string | null,
	image_width: number | null,
	image_height: number | null,
	video: UrlPreviewVideoView | null,
	theme_color: string | null,
	card: string | null,
	author_name: string | null,
};

export type UserDeviceView = {
	device_id: string,
	display_name: string | null,
	verified: boolean,
	cross_signed: boolean,
	blocked: boolean,
};

export type UserDirectoryEntryView = {
	user_id: string,
	display_name: string | null,
	avatar_url: string | null,
};

export type UserSecurityView = {
	verification: VerificationStateView,
	verification_violation: boolean,
	devices: UserDeviceView[],
};

export type UtdCauseView = "unknown" | "sent_before_we_joined" | "verification_violation" | "unsigned_device" | "unknown_device" | "historical_message_backup_disabled" | "historical_message_device_unverified" | "withheld_for_unverified_or_insecure_device" | "withheld_by_sender";

/**
 *  Mirrors `eyeball_im::VectorDiff`, which is not `Type` and whose `Vector<T>`
 *  has to flatten to a plain array on the wire.
 */
export type VectorDiff<T> = { op: "append"; values: T[] } | { op: "clear" } | { op: "push_front"; value: T } | { op: "push_back"; value: T } | { op: "pop_front" } | { op: "pop_back" } | { op: "insert"; index: number; value: T } | { op: "set"; index: number; value: T } | { op: "remove"; index: number } | { op: "truncate"; length: number } | { op: "reset"; values: T[] };

export type VerificationStateView = "unknown" | "verified" | "unverified";

/**
 *  The SDK splits this across a request and the SAS it becomes, with a state
 *  enum each. The UI shows one dialog, so both flatten into this sequence.
 */
export type VerificationView =
/**  Waiting for us to accept or decline. */
{ phase: "requested";
/**  Another of our own sessions, which also unlocks our history. */
is_self: boolean;
/**
 *  The current session sent the request, so it waits for the other
 *  device instead of showing an accept action.
 */
initiated_by_us: boolean } |
/**
 *  Nothing to do but wait. The side that accepted drives the transition to
 *  SAS.
 */
{ phase: "waiting" } |
/**
 *  Both sides are ready and support codes. `qr` is ours to show, and
 *  `can_scan` says the other device shows one we can read.
 */
{ phase: "choose"; qr: QrCodeView | null; can_scan: boolean; can_compare: boolean } |
/**  The other device read our code and waits for us to say it shows success. */
{ phase: "scanned" } | { phase: "reciprocated" } |
/**  `decimals` is the fallback when the other side refused emoji. */
{ phase: "compare"; emojis: EmojiView[]; decimals: [number, number, number] } |
/**  We said they match, but the other side has not. */
{ phase: "confirmed" } | { phase: "done" } | { phase: "cancelled"; reason: string };

export type ViaSplit = {
	href: string,
	via: string[],
};

/**  Bridge hints from a video's `info`: the `fi.mau.*` flags mautrix sets on gifs. */
export type VideoPlaybackView = {
	gif: boolean,
	autoplay: boolean,
	looping: boolean,
	hide_controls: boolean,
	no_audio: boolean,
};

export type WebPushKeys = {
	endpoint: string,
	p256dh: string,
	auth: string,
};

export type WebPushValidation = {
	app_id: string,
	ack_token: string,
};

/**  MSC4174 web push: the homeserver encrypts and delivers, so no gateway. */
export type WebPusherView = {
	pushkey: string,
	app_id: string,
	device_display_name: string,
	/**  The browser subscription, where a gateway pusher carries a `url`. */
	endpoint: string,
	auth: string,
	event_id_only: boolean,
};

export type ZonedInstant = {
	instant: number,
	datetime: string,
};

/** The core's methods by domain. */
export type Rpc = {
  account: {
    userProfile: (params: {
	user_id: string,
}) => Promise<ProfileView>;
    userRelations: (params: {
	user_id: string,
}) => Promise<{
	mutual_rooms: MutualRoomView[],
	ignored: boolean,
}>;
    dataTypes: (params?: Record<string, never>) => Promise<string[]>;
    data: (params: {
	event_type: string,
}) => Promise<unknown | null>;
    setData: (params: {
	event_type: string,
	content: unknown,
}) => Promise<null>;
    sealedData: (params: {
	event_type: string,
}) => Promise<SealedAccountDataView>;
    setSealedData: (params: {
	event_type: string,
	content: unknown,
}) => Promise<null>;
    setDisplayName: (params: {
	name?: string | null,
	propagate_to: ProfilePropagationView,
}) => Promise<null>;
    setAvatarUrl: (params: {
	url?: string | null,
	propagate_to: ProfilePropagationView,
}) => Promise<null>;
    setProfileField: (params: {
	field: string,
	value?: unknown | null,
}) => Promise<null>;
    contacts: (params?: Record<string, never>) => Promise<string[]>;
    ignoredUsers: (params?: Record<string, never>) => Promise<string[]>;
    inviteTriage: (params?: Record<string, never>) => Promise<InviteTriageView[]>;
    setDirect: (params: {
	room_id: string,
	direct: boolean,
	user_id?: string | null,
}) => Promise<null>;
    ignoreUser: (params: {
	user_id: string,
}) => Promise<null>;
    unignoreUser: (params: {
	user_id: string,
}) => Promise<null>;
    reportUser: (params: {
	user_id: string,
	reason: string,
}) => Promise<null>;
    deactivateAccount: (params: {
	password?: string | null,
}) => Promise<string | null>;
    setPresence: (params: {
	presence: PresenceView,
	status_message?: string | null,
}) => Promise<null>;
    setDeviceActivity: (params: {
	active: boolean,
}) => Promise<null>;
    fetchPresence: (params: {
	user_ids: string[],
}) => Promise<null>;
    searchUserDirectory: (params: {
	term: string,
	limit?: number | null,
}) => Promise<{
	limited: boolean,
	results: UserDirectoryEntryView[],
}>;
    dismissedInvites: (params?: Record<string, never>) => Promise<string[]>;
    setInviteDismissed: (params: {
	room_id: string,
	dismissed: boolean,
}) => Promise<string[]>;
    bookmarks: (params?: Record<string, never>) => Promise<BookmarkView[]>;
    setBookmark: (params: {
	room_id: string,
	event_id: string,
	bookmarked: boolean,
	now_ms: number,
}) => Promise<boolean>;
    mediaPreviewConfig: (params: {
	room_id?: string | null,
}) => Promise<MediaPreviewConfigView>;
    setMediaPreviewConfig: (params: {
	config: MediaPreviewConfigView,
}) => Promise<MediaPreviewConfigView>;
    setRoomMediaPreviews: (params: {
	room_id: string,
	media_previews: MediaPreviewsView | null,
}) => Promise<MediaPreviewConfigView>;
    dmSpaces: (params?: Record<string, never>) => Promise<DmSpaceLink[]>;
    setDmSpaces: (params: {
	room_id: string,
	space_ids: string[],
}) => Promise<DmSpaceLink[]>;
    profileOverrides: (params?: Record<string, never>) => Promise<ProfileOverridesView>;
    setProfileOverride: (params: {
	user_id: string,
	display_name: FieldEdit,
	avatar: AvatarEditView,
	color: FieldEdit,
}) => Promise<ProfileOverridesView>;
  };
  auth: {
    discoverHomeserver: (params: {
	server_name: string,
}) => Promise<string>;
    login: (params: {
	reauth_account_id?: string | null,
	homeserver: string,
	identifier: LoginIdentifier,
	password: string,
}) => Promise<string>;
    loginFlows: (params: {
	homeserver: string,
}) => Promise<LoginFlowsView>;
    registrationFlows: (params: {
	homeserver: string,
}) => Promise<RegistrationFlowsView>;
    register: (params: {
	homeserver: string,
	username: string,
	password: string,
	registration_email?: string | null,
	registration_token: string | null,
}) => Promise<RegistrationResultView>;
    requestRegistrationEmail: (params: {
	email: string,
}) => Promise<RegistrationResultView>;
    submitRegistrationEmail: (params: {
	token: string,
}) => Promise<RegistrationResultView>;
    continueRegistration: (params?: Record<string, never>) => Promise<RegistrationResultView>;
    cancelRegistration: (params?: Record<string, never>) => Promise<null>;
    requestPasswordResetEmail: (params: {
	homeserver: string,
	email: string,
	client_secret?: string | null,
	send_attempt: number,
}) => Promise<{
	client_secret: string,
	sid: string,
}>;
    resetPassword: (params: {
	homeserver: string,
	client_secret: string,
	sid: string,
	new_password: string,
	logout_devices: boolean,
}) => Promise<null>;
    startOidcLogin: (params: {
	reauth_account_id?: string | null,
	homeserver: string,
	redirect_uri: string,
	intent: AuthIntent,
}) => Promise<string>;
    completeOidcLogin: (params: {
	callback_url: string,
}) => Promise<string>;
    startSsoLogin: (params: {
	reauth_account_id?: string | null,
	homeserver: string,
	redirect_uri: string,
	idp_id?: string | null,
	intent: AuthIntent,
}) => Promise<string>;
    completeSsoLogin: (params: {
	callback_url: string,
}) => Promise<string>;
    startQrLogin: (params: {
	homeserver?: string | null,
	redirect_uri: string,
	scanned: string | null,
}) => Promise<null>;
    startQrGrant: (params: {
	scanned?: string | null,
}) => Promise<null>;
    qrCheckCode: (params: {
	code: number,
}) => Promise<null>;
    qrGrantContinue: (params: {
	confirm: boolean,
}) => Promise<null>;
    cancelQr: (params?: Record<string, never>) => Promise<null>;
  };
  calendar: {
    entries: (params: {
	room_id: string,
}) => Promise<CalendarView>;
    saveEvent: (params: {
	room_id: string,
	event: unknown,
	replaces?: string | null,
}) => Promise<null>;
    answerEvent: (params: {
	room_id: string,
	calendar_event_id: string,
	uid: string,
	recurrence_id?: string | null,
	status: RsvpStatusView,
}) => Promise<null>;
  };
  calls: {
    turnServer: (params?: Record<string, never>) => Promise<TurnServerView>;
    rtcTransports: (params?: Record<string, never>) => Promise<unknown>;
    rtcLivekit: (params: {
	endpoint: RtcLivekitEndpoint,
	body: unknown,
}) => Promise<unknown>;
    join: (params: {
	room_id: string,
	livekit_service_url?: string | null,
	mode?: CallMode | null,
	intent?: CallIntent | null,
}) => Promise<{
	session: CallSessionId,
	url: string,
	jwt: string,
	identity: string,
	encrypt_media: boolean,
	mode: CallMode,
	mode_reason: CallModeReason,
	sticky_available: boolean,
	peer_modes: CallMode[],
	can_publish: boolean,
	publisher_id: string,
	backends: CallBackendView[],
}>;
    compatibility: (params?: Record<string, never>) => Promise<CallCompatibilityView>;
    support: (params: {
	room_id: string,
	livekit_service_url?: string | null,
}) => Promise<CallSupportView>;
    leave: (params: {
	session: CallSessionId,
}) => Promise<null>;
    setScreenSharing: (params: {
	session: CallSessionId,
	active: boolean,
}) => Promise<null>;
    decline: (params: {
	room_id: string,
	notification_event_id: string,
}) => Promise<null>;
  };
  encryption: {
    status: (params?: Record<string, never>) => Promise<EncryptionStatusView>;
    keyBackupStatus: (params?: Record<string, never>) => Promise<KeyBackupStatusView>;
    downloadKeyBackup: (params: {
	request_id: string,
}) => Promise<KeyBackupDownloadView>;
    signOutSafety: (params?: Record<string, never>) => Promise<SignOutSafetyView>;
    devices: (params?: Record<string, never>) => Promise<{
	devices: DeviceView[],
}>;
    userSecurity: (params: {
	user_id: string,
}) => Promise<UserSecurityView>;
    recoverIdentity: (params: {
	recovery_key: string,
}) => Promise<null>;
    enableRecovery: (params: {
	/**  A passphrase to unlock the key with. The key is returned either way. */
	passphrase?: string | null,
}) => Promise<string>;
    resetRecoveryKey: (params: {
	passphrase?: string | null,
}) => Promise<string>;
    resetIdentity: (params?: Record<string, never>) => Promise<IdentityResetStep>;
    continueIdentityReset: (params: {
	password?: string | null,
}) => Promise<string>;
    cancelIdentityReset: (params?: Record<string, never>) => Promise<null>;
    exportRoomKeys: (params: {
	passphrase: string,
	room_id?: string | null,
}) => Promise<string>;
    importRoomKeys: (params: {
	export: string,
	passphrase: string,
}) => Promise<{
	imported: number,
	total: number,
}>;
    deleteDevice: (params: {
	device_id: string,
	password?: string | null,
}) => Promise<string | null>;
    renameDevice: (params: {
	device_id: string,
	display_name: string,
}) => Promise<null>;
    discardRoomKey: (params: {
	room_id: string,
}) => Promise<null>;
    shareHistory: (params: {
	room_id: string,
	user_id: string,
}) => Promise<ShareHistoryView>;
    requestVerification: (params: {
	user_id: string,
	device_id?: string | null,
}) => Promise<string>;
    withdrawVerification: (params: {
	user_id: string,
}) => Promise<null>;
    setDeviceBlocked: (params: {
	user_id: string,
	device_id: string,
	blocked: boolean,
}) => Promise<null>;
    acceptVerification: (params: {
	user_id: string,
	flow_id: string,
}) => Promise<null>;
    scanVerificationQr: (params: {
	user_id: string,
	flow_id: string,
	data: string,
}) => Promise<null>;
    startSasVerification: (params: {
	user_id: string,
	flow_id: string,
}) => Promise<null>;
    confirmVerification: (params: {
	user_id: string,
	flow_id: string,
}) => Promise<null>;
    cancelVerification: (params: {
	user_id: string,
	flow_id: string,
	/**
	 *  The emoji differed. An attack signal the other side must be told
	 *  about. A plain cancel is not.
	 */
	mismatch: boolean,
}) => Promise<null>;
  };
  members: {
    list: (params: {
	room_id: string,
	memberships?: MembershipView[],
}) => Promise<MemberView[]>;
    permissions: (params: {
	room_id: string,
}) => Promise<RoomPermissionsView>;
    powerLevels: (params: {
	room_id: string,
}) => Promise<RoomPowerLevelsView>;
    setPowerLevel: (params: {
	room_id: string,
	levels?: RoomPowerLevelsView | null,
	location: PowerLevelLocation,
	level: number,
}) => Promise<RoomPowerLevelsView>;
    syncPowerLevelsFromSpace: (params: {
	room_id: string,
	space_id: string,
	levels?: RoomPowerLevelsView | null,
}) => Promise<{
	levels: RoomPowerLevelsView,
	tags: { [key in string]: PowerLevelTagView },
}>;
    syncChildPowerLevels: (params: {
	space_id: string,
	child_ids: string[],
}) => Promise<{
	updated: number,
	skipped: number,
}>;
    powerLevelTags: (params: {
	room_id: string,
}) => Promise<{ [key in string]: PowerLevelTagView }>;
    setPowerLevelTag: (params: {
	room_id: string,
	level: number,
	tag?: PowerLevelTagView | null,
}) => Promise<{ [key in string]: PowerLevelTagView }>;
    setUserPowerLevel: (params: {
	room_id: string,
	user_id: string,
	power_level: number,
}) => Promise<null>;
    kick: (params: {
	room_id: string,
	user_id: string,
	reason?: string | null,
}) => Promise<null>;
    denyKnock: (params: {
	room_id: string,
	user_id: string,
}) => Promise<null>;
    ban: (params: {
	room_id: string,
	user_id: string,
	reason?: string | null,
}) => Promise<null>;
    unban: (params: {
	room_id: string,
	user_id: string,
	reason?: string | null,
}) => Promise<null>;
    look: (params: {
	room_id: string,
}) => Promise<MemberLookView>;
    setLook: (params: {
	room_id: string,
	in_parent_space?: boolean,
	display_name?: FieldEdit,
	avatar_url?: FieldEdit,
	color_on_light?: FieldEdit,
	color_on_dark?: FieldEdit,
}) => Promise<null>;
    setPronouns: (params: {
	room_id: string,
	in_parent_space?: boolean,
	pronouns: PronounView[],
}) => Promise<null>;
  };
  messages: {
    markdownToContent: (params: {
	markdown: string,
	pills?: ComposerPill[],
}) => Promise<ComposerContent>;
    send: (params: {
	room_id: string,
	body: string,
	formatted?: string | null,
	kind?: MessageKind,
	thread_root?: string | null,
	/**
	 *  Replying inside a thread needs no extra field: the SDK infers the
	 *  thread from the replied-to event.
	 */
	in_reply_to?: string | null,
	mentions?: string[],
	mentions_room?: boolean,
	silent_reply?: boolean,
	persona?: PerMessageProfileView | null,
	link_previews?: UrlPreviewView[],
	no_link_previews?: boolean,
	image_source_packs?: ImageSourcePackReferenceView[],
	bot_command?: unknown | null,
	forum_title?: string | null,
}) => Promise<null>;
    sendRawEvent: (params: {
	room_id: string,
	event_type: string,
	content: unknown,
	persona?: PerMessageProfileView | null,
}) => Promise<string>;
    sendRedaction: (params: {
	room_id: string,
	event_id: string,
	reason?: string | null,
}) => Promise<string>;
    sendSticker: (params: {
	room_id: string,
	url: string,
	body: string,
	info?: PackImageInfoView | null,
	source_pack?: ImageSourcePackView | null,
	in_reply_to?: string | null,
	thread_root?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    sendGif: (params: {
	room_id: string,
	/**  `mxc://` only; the core rejects anything else. */
	url: string,
	body: string,
	width?: number | null,
	height: number | null,
	mimetype: string,
	size?: number | null,
	in_reply_to?: string | null,
	silent_reply?: boolean,
	thread_root?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    removeLinkPreviews: (params: {
	room_id: string,
	event_id: string,
	thread_root?: string | null,
}) => Promise<null>;
    reprofile: (params: {
	room_id: string,
	event_id: string,
	thread_root?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    edit: (params: {
	room_id: string,
	event_id?: string | null,
	transaction_id?: string | null,
	body: string,
	formatted: string | null,
	kind?: MessageKind,
	media_caption?: boolean,
	thread_root?: string | null,
	mentions?: string[],
	mentions_room?: boolean,
	persona?: PerMessageProfileView | null,
	forum_title?: string | null,
}) => Promise<null>;
    redact: (params: {
	room_id: string,
	event_id: string,
	reason?: string | null,
	thread_root?: string | null,
}) => Promise<null>;
    deleteThread: (params: {
	room_id: string,
	root_event_id: string,
	reason?: string | null,
}) => Promise<null>;
    bulkRedact: (params: {
	room_id: string,
	senders: string[],
	after_ts: number,
	event_types: string[],
	reason?: string | null,
}) => Promise<number>;
    react: (params: {
	room_id: string,
	event_id: string,
	key: string,
	source_pack?: ImageSourcePackView | null,
	shortcode?: string | null,
	thread_root?: string | null,
	subscription?: SubscriptionId | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    sendLocation: (params: {
	room_id: string,
	body: string,
	geo_uri: string,
	in_reply_to?: string | null,
	silent_reply?: boolean,
	thread_root?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    forward: (params: {
	room_id: string,
	event_id: string,
	to_room_id: string,
	include_metadata?: boolean | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    report: (params: {
	room_id: string,
	event_id: string,
	reason?: string | null,
}) => Promise<null>;
    retrySend: (params: {
	room_id: string,
	transaction_id: string,
	thread_root?: string | null,
}) => Promise<null>;
    cancelSend: (params: {
	room_id: string,
	transaction_id: string,
	thread_root?: string | null,
}) => Promise<boolean>;
    schedule: (params: {
	room_id: string,
	body: string,
	formatted?: string | null,
	delay_ms: number,
	persona?: PerMessageProfileView | null,
}) => Promise<string>;
    scheduleAttachment: (params: {
	room_id: string,
	filename: string,
	mime: string,
	url: string,
	size: number,
	info?: AttachmentInfoView | null,
	spoiler?: boolean,
	delay_ms: number,
	persona?: PerMessageProfileView | null,
}) => Promise<string>;
    scheduled: (params: {
	room_id?: string | null,
}) => Promise<ScheduledMessageView[]>;
    cancelScheduled: (params: {
	delay_id: string,
}) => Promise<null>;
    sendScheduled: (params: {
	delay_id: string,
}) => Promise<null>;
    createPoll: (params: {
	room_id: string,
	question: string,
	answers: string[],
	/**  Withholds the tally until the poll closes. */
	undisclosed: boolean,
	max_selections: number,
	thread_root?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    votePoll: (params: {
	room_id: string,
	/**  The poll's start event. */
	event_id: string,
	/**  The ids of the chosen answers. */
	answers: string[],
	thread_root?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    endPoll: (params: {
	room_id: string,
	event_id: string,
	thread_root?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    sendGesture: (params: {
	room_id: string,
	gesture: GestureView,
	target?: string | null,
	persona?: PerMessageProfileView | null,
}) => Promise<null>;
    urlPreview: (params: {
	url: string,
	service?: string | null,
}) => Promise<UrlPreviewView | null>;
    reactionShortcodes: (params: {
	room_id: string,
	event_id: string,
}) => Promise<ReactionShortcodeView[]>;
    frequentReactions: (params: {
	legacy?: unknown | null,
}) => Promise<string[]>;
    setTyping: (params: {
	room_id: string,
	typing: boolean,
}) => Promise<null>;
    mediaConfig: (params?: Record<string, never>) => Promise<{
	upload_size: number,
}>;
  };
  notifications: {
    settings: (params: {
	room_id: string,
}) => Promise<NotificationSettingsView>;
    defaultModes: (params?: Record<string, never>) => Promise<DefaultNotificationModesView>;
    mentionNotifications: (params?: Record<string, never>) => Promise<MentionNotificationsView>;
    eventNotifications: (params?: Record<string, never>) => Promise<EventNotificationsView>;
    masterMute: (params?: Record<string, never>) => Promise<boolean | null>;
    notification: (params: {
	room_id: string,
	event_id: string,
}) => Promise<NotificationView | null>;
    pushEvent: (params: {
	room_id: string,
	event_id: string,
}) => Promise<PushFetchView>;
    keywords: (params?: Record<string, never>) => Promise<KeywordNotificationView[]>;
    addKeyword: (params: {
	keyword: string,
}) => Promise<null>;
    removeKeyword: (params: {
	keyword: string,
}) => Promise<null>;
    setKeywordMode: (params: {
	keyword: string,
	mode: MentionNotificationModeView,
}) => Promise<null>;
    setRoomNotificationMode: (params: {
	room_id: string,
	/**  `null` drops the room's own rules so it follows the default again. */
	mode?: NotificationModeView | null,
}) => Promise<null>;
    setDefaultMode: (params: {
	direct: boolean,
	mode: NotificationModeView,
}) => Promise<null>;
    setMentionNotifications: (params: {
	rule: MentionRuleView,
	mode: MentionNotificationModeView,
}) => Promise<null>;
    setEventNotification: (params: {
	event: EventNotificationView,
	enabled: boolean,
}) => Promise<null>;
    setMasterMute: (params: {
	muted: boolean,
}) => Promise<null>;
    inbox: (params: {
	filter: InboxFilter,
	include_read: boolean,
	limit: number,
	before_ts?: number | null,
}) => Promise<{
	items: InboxItemView[],
	has_more: boolean,
}>;
    backfillInbox: (params: {
	include_read: boolean,
}) => Promise<{
	recorded: number,
	has_more: boolean,
}>;
    setContent: (params: {
	visible: boolean,
	encrypted: boolean,
}) => Promise<null>;
    setSounds: (params: {
	enabled: boolean,
}) => Promise<null>;
    setNotifyOnce: (params: {
	enabled: boolean,
}) => Promise<null>;
    setEnabled: (params: {
	enabled: boolean,
}) => Promise<null>;
    pushRules: (params?: Record<string, never>) => Promise<unknown>;
    setPushRuleEnabled: (params: {
	kind: string,
	rule_id: string,
	enabled: boolean,
}) => Promise<null>;
    setPushRuleActions: (params: {
	kind: string,
	rule_id: string,
	actions: unknown[],
}) => Promise<null>;
    deletePushRule: (params: {
	kind: string,
	rule_id: string,
}) => Promise<null>;
  };
  packs: {
    list: (params: {
	room_id: string,
	cached_only?: boolean,
}) => Promise<{
	packs: ImagePackView[],
	complete: boolean,
}>;
    listAll: (params?: Record<string, never>) => Promise<ImagePackView[]>;
    save: (params: {
	target: PackTargetView,
	draft: PackDraftView,
}) => Promise<null>;
    createRoomPack: (params: {
	room_id: string,
	name: string,
}) => Promise<string>;
    deleteRoomPack: (params: {
	room_id: string,
	state_key: string,
}) => Promise<null>;
    convertLegacy: (params: {
	room_id: string,
	state_keys: string[],
}) => Promise<null>;
    removeLegacy: (params: {
	room_id: string,
	state_keys: string[],
}) => Promise<null>;
    addAccountImages: (params: {
	images: PackImageDraftView[],
}) => Promise<null>;
    subscriptions: (params?: Record<string, never>) => Promise<PackAddressView[]>;
    setSubscriptions: (params: {
	added: PackAddressView[],
	removed: PackAddressView[],
}) => Promise<PackAddressView[]>;
  };
  personas: {
    list: (params?: Record<string, never>) => Promise<PersonaCatalogView>;
    save: (params: {
	persona: PersonaView,
	base_persona: PersonaView,
	previous_id?: string | null,
}) => Promise<PersonaView[]>;
    remove: (params: {
	id: string,
}) => Promise<PersonaView[]>;
    reorder: (params: {
	ids: string[],
}) => Promise<PersonaView[]>;
    setSelection: (params: {
	room_id?: string | null,
	persona_id: string | null,
	alternate_avatar_id?: string | null,
	valid_until?: number | null,
}) => Promise<null>;
    disableRoom: (params: {
	room_id: string,
}) => Promise<null>;
    backup: (params?: Record<string, never>) => Promise<string>;
    checkBackup: (params: {
	backup: string,
}) => Promise<null>;
    restoreBackup: (params: {
	backup: string,
}) => Promise<null>;
  };
  push: {
    setPusher: (params: {
	pusher: PusherView,
}) => Promise<null>;
    removePusher: (params: {
	pushkey: string,
	app_id: string,
}) => Promise<null>;
    webPusherSupport: (params?: Record<string, never>) => Promise<string | null>;
    setWebPusher: (params: {
	pusher: WebPusherView,
}) => Promise<null>;
    webPushers: (params?: Record<string, never>) => Promise<RegisteredPusherView[]>;
    pingGateway: (params: {
	url: string,
}) => Promise<boolean | null>;
    discoverGateway: (params: {
	endpoint: string,
}) => Promise<string | null>;
    sendDiagnostic: (params: {
	pushkey: string,
	app_id: string,
}) => Promise<DiagnosticPushView>;
    ackWebPusher: (params: {
	app_id: string,
	ack_token: string,
}) => Promise<null>;
  };
  room_state: {
    setName: (params: {
	room_id: string,
	name?: string | null,
}) => Promise<null>;
    setTopic: (params: {
	room_id: string,
	topic: string,
}) => Promise<null>;
    setAvatar: (params: {
	room_id: string,
	url?: string | null,
}) => Promise<null>;
    setJoinRule: (params: {
	room_id: string,
	rule: JoinRuleView,
}) => Promise<null>;
    historyVisibility: (params: {
	room_id: string,
}) => Promise<HistoryVisibilityView>;
    setHistoryVisibility: (params: {
	room_id: string,
	visibility: HistoryVisibilityView,
}) => Promise<null>;
    enableEncryption: (params: {
	room_id: string,
}) => Promise<null>;
    banner: (params: {
	room_id: string,
}) => Promise<string | null>;
    setBanner: (params: {
	room_id: string,
	url?: string | null,
}) => Promise<null>;
    memberListThreshold: (params: {
	room_id: string,
}) => Promise<number | null>;
    setMemberListThreshold: (params: {
	room_id: string,
	always_listed_from?: number | null,
}) => Promise<null>;
    updateServerAcl: (params: {
	room_id: string,
	allow: string[],
	deny: string[],
	remove_allow: string[],
	remove_deny: string[],
}) => Promise<null>;
    abbreviations: (params: {
	room_id: string,
}) => Promise<AbbreviationView[]>;
    setAbbreviations: (params: {
	room_id: string,
	entries: AbbreviationView[],
}) => Promise<null>;
    event: (params: {
	room_id: string,
	event_type: string,
	state_key: string,
}) => Promise<unknown | null>;
    events: (params: {
	room_id: string,
	event_type: string,
}) => Promise<RoomStateEventView[]>;
    eventsRaw: (params: {
	room_id: string,
	event_type: string,
	state_key?: string | null,
}) => Promise<unknown[]>;
    fullState: (params: {
	room_id: string,
}) => Promise<unknown[]>;
    sendEvent: (params: {
	room_id: string,
	event_type: string,
	state_key: string,
	content: unknown,
}) => Promise<string>;
    accountData: (params: {
	room_id: string,
	event_type: string,
}) => Promise<unknown | null>;
    accountDataTypes: (params: {
	room_id: string,
}) => Promise<string[]>;
    setAccountData: (params: {
	room_id: string,
	event_type: string,
	content: unknown,
}) => Promise<null>;
    accountDataRaw: (params: {
	room_id: string,
	event_type: string,
}) => Promise<unknown | null>;
    addWidget: (params: {
	room_id: string,
	name: string,
	url: string,
}) => Promise<string>;
    removeWidget: (params: {
	room_id: string,
	widget_id: string,
}) => Promise<null>;
    botCommands: (params: {
	room_id: string,
}) => Promise<BotCommand[]>;
  };
  rooms: {
    open: (params: {
	room_id: string,
}) => Promise<RoomOpenView>;
    listItem: (params: {
	room_id: string,
}) => Promise<RoomListItemView>;
    preview: (params: {
	/**  A room id or an alias, as `JoinRoom` takes. */
	address: string,
	/**  Servers to try when the id is not resolvable on ours, or empty. */
	via: string[],
}) => Promise<RoomPreviewView>;
    join: (params: {
	/**  A room id or an alias. A pasted address could be either. */
	address: string,
	/**  Servers to try when the id is not resolvable on ours, or empty. */
	via: string[],
}) => Promise<string>;
    knock: (params: {
	address: string,
	via: string[],
	reason?: string | null,
}) => Promise<string>;
    viaServers: (params: {
	room_id: string,
}) => Promise<string[]>;
    leave: (params: {
	room_id: string,
}) => Promise<null>;
    invite: (params: {
	room_id: string,
	user_id: string,
}) => Promise<null>;
    create: (params: {
	name?: string | null,
	topic: string | null,
	kind: RoomKind,
	/**  Published in the directory, joinable by link. */
	public: boolean,
	/**  Ignored for a space or a public room. */
	encrypted: boolean,
	invite: string[],
	/**  Adds an `m.space.child` edge from this space. */
	parent_space?: string | null,
	alias: string | null,
	room_version: string | null,
	join_rule?: CreateJoinRuleView | null,
	federate: boolean,
	predecessor?: string | null,
}) => Promise<string>;
    createDm: (params: {
	user_id: string,
	encrypted?: boolean | null,
}) => Promise<string>;
    versions: (params?: Record<string, never>) => Promise<RoomVersionsView>;
    upgrade: (params: {
	room_id: string,
	new_version: string,
	additional_creators: string[],
}) => Promise<string>;
    publicRooms: (params: {
	server?: string | null,
	search: string | null,
	since: string | null,
	room_type?: DirectoryRoomType | null,
}) => Promise<{
	rooms: PublicRoomView[],
	next_batch: string | null,
	total: number | null,
}>;
    directoryVisibility: (params: {
	room_id: string,
}) => Promise<boolean>;
    setDirectoryVisibility: (params: {
	room_id: string,
	public: boolean,
}) => Promise<null>;
    aliases: (params: {
	room_id: string,
}) => Promise<string[]>;
    createAlias: (params: {
	room_id: string,
	alias: string,
}) => Promise<null>;
    deleteAlias: (params: {
	alias: string,
}) => Promise<null>;
    canonicalAlias: (params: {
	room_id: string,
}) => Promise<{
	alias: string | null,
	alt_aliases: string[],
}>;
    setCanonicalAlias: (params: {
	room_id: string,
	alias?: string | null,
	alt_aliases: string[],
}) => Promise<{
	alias: string | null,
	alt_aliases: string[],
}>;
    replacedRooms: (params?: Record<string, never>) => Promise<RoomListItemView[]>;
    setTombstone: (params: {
	room_id: string,
	replacement_room: string,
	body: string,
}) => Promise<null>;
    report: (params: {
	room_id: string,
	reason: string,
}) => Promise<null>;
    setTag: (params: {
	room_id: string,
	tag: RoomTag,
	/**  False removes it. */
	set: boolean,
}) => Promise<null>;
    attachments: (params: {
	room_id: string,
	kind: RoomAttachmentKind,
	limit: number,
	from?: string | null,
}) => Promise<{
	items: RoomAttachmentView[],
	next_batch: string | null,
}>;
    cosmetics: (params: {
	room_id: string,
	space_id?: string | null,
}) => Promise<RoomCosmeticsView>;
  };
  search: {
    messages: (params: {
	query: string,
	filter?: SearchFilter,
	order?: SearchOrder,
	limit: number,
	offset: number,
	context?: number,
	older?: string | null,
}) => Promise<{
	hits: SearchHitView[],
	older: string | null,
}>;
    coverage: (params?: Record<string, never>) => Promise<SearchCoverageView>;
    metrics: (params?: Record<string, never>) => Promise<SearchMetricsView>;
  };
  session: {
    restore: (params?: Record<string, never>) => Promise<SessionInfo | null>;
    listAccounts: (params?: Record<string, never>) => Promise<SessionInfo[]>;
    switchAccount: (params: {
	account_id: string,
}) => Promise<SessionInfo>;
    removeAccount: (params: {
	account_id: string,
}) => Promise<null>;
    logout: (params?: Record<string, never>) => Promise<null>;
    resetLocalCache: (params?: Record<string, never>) => Promise<null>;
    homeserverInfo: (params?: Record<string, never>) => Promise<{
	homeserver: string,
	server: HomeserverSoftwareView | null,
}>;
    oauthMetadata: (params?: Record<string, never>) => Promise<OAuthMetadataView | null>;
    requestOpenIdToken: (params?: Record<string, never>) => Promise<{
	access_token: string,
	matrix_server_name: string,
	expires_in: number,
}>;
    accessToken: (params?: Record<string, never>) => Promise<string | null>;
    openIdToken: (params?: Record<string, never>) => Promise<OpenIdTokenView>;
    syncStatus: (params?: Record<string, never>) => Promise<SyncStatus>;
    serverFeatures: (params?: Record<string, never>) => Promise<string[]>;
    delayedEventsSupported: (params?: Record<string, never>) => Promise<boolean>;
    setSearchOptions: (params: {
	disk_budget_mb: number,
	crawler: boolean,
	unmetered_only: boolean,
	server_search: boolean,
	tuning: SearchTuning,
	foreground: boolean,
}) => Promise<null>;
    setReadRoom: (params: {
	room_id?: string | null,
}) => Promise<null>;
    integrationManagerUrl: (params: {
	room_id: string,
}) => Promise<string>;
    integrationManagerTerms: (params?: Record<string, never>) => Promise<TermsPolicyView[]>;
    acceptIntegrationManagerTerms: (params: {
	urls: string[],
}) => Promise<null>;
    knownRooms: (params?: Record<string, never>) => Promise<string[]>;
  };
  spaces: {
    add: (params: {
	space_id: string,
	room_id: string,
	suggested?: boolean | null,
}) => Promise<null>;
    setChildOrder: (params: {
	space_id: string,
	room_id: string,
	order?: string | null,
}) => Promise<null>;
    setChildSuggested: (params: {
	space_id: string,
	room_id: string,
	suggested: boolean,
}) => Promise<null>;
    hierarchy: (params: {
	space_id: string,
	from?: string | null,
}) => Promise<{
	rooms: SpaceHierarchyRoomView[],
	/**  Pass back as `from` for the next page; `null` at the end. */
	next_batch: string | null,
}>;
    remove: (params: {
	space_id: string,
	room_id: string,
}) => Promise<null>;
    sidebar: (params?: Record<string, never>) => Promise<SidebarItemView[]>;
    setSidebar: (params: {
	items: SidebarItemView[],
}) => Promise<null>;
    roomHasSpaceParent: (params: {
	room_id: string,
}) => Promise<boolean>;
    unjoinedSpaceParents: (params: {
	room_id: string,
}) => Promise<SpaceParentView[]>;
  };
  timeline: {
    subscribeRoomList: (params?: Record<string, never>) => Promise<{
	subscription: SubscriptionId,
	rooms: RoomListItemView[],
}>;
    subscribe: (params: {
	room_id: string,
	focus?: TimelineFocusView,
	/**
	 *  Relaxes the event filter so events the SDK would otherwise drop
	 *  arrive as `HiddenEvent`. Baked into the timeline, so flipping it
	 *  means re-subscribing.
	 */
	hidden_events: boolean,
}) => Promise<{
	subscription: SubscriptionId,
	items: TimelineItemView[],
	aggregations: TimelineItemView[],
}>;
    unsubscribe: (params: {
	subscription: SubscriptionId,
}) => Promise<null>;
    warmRooms: (params: {
	room_ids: string[],
}) => Promise<null>;
    paginate: (params: {
	subscription: SubscriptionId,
	direction: PaginationDirection,
	count: number,
}) => Promise<{
	direction: PaginationDirection,
	reached_end: boolean,
}>;
    fetchEventDetails: (params: {
	room_id: string,
	event_id: string,
	thread_root?: string | null,
}) => Promise<null>;
    eventItems: (params: {
	room_id: string,
	event_ids: string[],
}) => Promise<TimelineItemView[]>;
    events: (params: {
	room_id: string,
	event_type: string,
	msgtype?: string | null,
	state_key?: string | null,
	limit: number,
	since?: string | null,
}) => Promise<unknown[]>;
    timestampToEvent: (params: {
	room_id: string,
	ts: number,
	direction: PaginationDirection,
}) => Promise<string | null>;
    listThreads: (params: {
	room_id: string,
	from?: string | null,
}) => Promise<{
	roots: TimelineItemView[],
	next_batch: string | null,
}>;
    eventSource: (params: {
	room_id: string,
	event_id: string,
	include_original: boolean,
}) => Promise<{
	source: string,
	encrypted: boolean,
	original: string | null,
	encryption: string | null,
}>;
    editHistory: (params: {
	room_id: string,
	event_id: string,
}) => Promise<EditVersionView[]>;
    markRead: (params: {
	room_id: string,
	event_id?: string | null,
	private_receipt?: boolean,
	thread_root?: string | null,
	subscription?: SubscriptionId | null,
	fully_read?: boolean,
}) => Promise<null>;
    setFullyRead: (params: {
	room_id: string,
	event_id: string,
}) => Promise<null>;
    markUnread: (params: {
	room_id: string,
	read_marker?: string | null,
}) => Promise<null>;
    readMarker: (params: {
	room_id: string,
}) => Promise<string | null>;
    unreadDiagnostics: (params: {
	thread_roots?: string[],
	room_id: string,
}) => Promise<UnreadDiagnosticsView>;
    retryDecryption: (params: {
	room_id: string,
	session_id: string,
	sender: string,
	thread_root?: string | null,
}) => Promise<null>;
    roomEventRelations: (params: {
	room_id: string,
	event_id: string,
	rel_type?: string | null,
	event_type?: string | null,
	from?: string | null,
	to?: string | null,
	limit?: number | null,
	direction?: PaginationDirection | null,
}) => Promise<RelationsView>;
    pinnedEvents: (params: {
	room_id: string,
}) => Promise<string[]>;
    setPinned: (params: {
	room_id: string,
	event_id: string,
	pinned: boolean,
}) => Promise<string[]>;
    pinReadState: (params: {
	room_id: string,
}) => Promise<PinReadStateView>;
    markPinsSeen: (params: {
	room_id: string,
	pinned_ids: string[],
}) => Promise<PinReadStateView>;
  };
  widgets: {
    sendDelayedEvent: (params: {
	room_id: string,
	event_type: string,
	state_key?: string | null,
	content: unknown,
	delay_ms: number,
	sticky_duration_ms?: number | null,
}) => Promise<string>;
    sendStickyEvent: (params: {
	room_id: string,
	event_type: string,
	content: unknown,
	sticky_duration_ms: number,
}) => Promise<string>;
    restartDelayedEvent: (params: {
	delay_id: string,
}) => Promise<null>;
    sendToDevice: (params: {
	event_type: string,
	encrypted: boolean,
	messages: unknown,
}) => Promise<null>;
    roomStickyEvents: (params: {
	room_id: string,
}) => Promise<unknown[]>;
    setWidgetFeed: (params: {
	enabled: boolean,
}) => Promise<null>;
  };
};

type RpcMethod = (params: never) => Promise<unknown>;

export type RpcParams<M extends RpcMethod> = NonNullable<Parameters<M>[0]>;

export type RpcResult<M extends RpcMethod> = Awaited<ReturnType<M>>;

/** Each method by its wire name, for code that speaks the wire format. */
export type RpcMethods = {
  'account.user_profile': Rpc['account']['userProfile'];
  'account.user_relations': Rpc['account']['userRelations'];
  'account.data_types': Rpc['account']['dataTypes'];
  'account.data': Rpc['account']['data'];
  'account.set_data': Rpc['account']['setData'];
  'account.sealed_data': Rpc['account']['sealedData'];
  'account.set_sealed_data': Rpc['account']['setSealedData'];
  'account.set_display_name': Rpc['account']['setDisplayName'];
  'account.set_avatar_url': Rpc['account']['setAvatarUrl'];
  'account.set_profile_field': Rpc['account']['setProfileField'];
  'account.contacts': Rpc['account']['contacts'];
  'account.ignored_users': Rpc['account']['ignoredUsers'];
  'account.invite_triage': Rpc['account']['inviteTriage'];
  'account.set_direct': Rpc['account']['setDirect'];
  'account.ignore_user': Rpc['account']['ignoreUser'];
  'account.unignore_user': Rpc['account']['unignoreUser'];
  'account.report_user': Rpc['account']['reportUser'];
  'account.deactivate_account': Rpc['account']['deactivateAccount'];
  'account.set_presence': Rpc['account']['setPresence'];
  'account.set_device_activity': Rpc['account']['setDeviceActivity'];
  'account.fetch_presence': Rpc['account']['fetchPresence'];
  'account.search_user_directory': Rpc['account']['searchUserDirectory'];
  'account.dismissed_invites': Rpc['account']['dismissedInvites'];
  'account.set_invite_dismissed': Rpc['account']['setInviteDismissed'];
  'account.bookmarks': Rpc['account']['bookmarks'];
  'account.set_bookmark': Rpc['account']['setBookmark'];
  'account.media_preview_config': Rpc['account']['mediaPreviewConfig'];
  'account.set_media_preview_config': Rpc['account']['setMediaPreviewConfig'];
  'account.set_room_media_previews': Rpc['account']['setRoomMediaPreviews'];
  'account.dm_spaces': Rpc['account']['dmSpaces'];
  'account.set_dm_spaces': Rpc['account']['setDmSpaces'];
  'account.profile_overrides': Rpc['account']['profileOverrides'];
  'account.set_profile_override': Rpc['account']['setProfileOverride'];
  'auth.discover_homeserver': Rpc['auth']['discoverHomeserver'];
  'auth.login': Rpc['auth']['login'];
  'auth.login_flows': Rpc['auth']['loginFlows'];
  'auth.registration_flows': Rpc['auth']['registrationFlows'];
  'auth.register': Rpc['auth']['register'];
  'auth.request_registration_email': Rpc['auth']['requestRegistrationEmail'];
  'auth.submit_registration_email': Rpc['auth']['submitRegistrationEmail'];
  'auth.continue_registration': Rpc['auth']['continueRegistration'];
  'auth.cancel_registration': Rpc['auth']['cancelRegistration'];
  'auth.request_password_reset_email': Rpc['auth']['requestPasswordResetEmail'];
  'auth.reset_password': Rpc['auth']['resetPassword'];
  'auth.start_oidc_login': Rpc['auth']['startOidcLogin'];
  'auth.complete_oidc_login': Rpc['auth']['completeOidcLogin'];
  'auth.start_sso_login': Rpc['auth']['startSsoLogin'];
  'auth.complete_sso_login': Rpc['auth']['completeSsoLogin'];
  'auth.start_qr_login': Rpc['auth']['startQrLogin'];
  'auth.start_qr_grant': Rpc['auth']['startQrGrant'];
  'auth.qr_check_code': Rpc['auth']['qrCheckCode'];
  'auth.qr_grant_continue': Rpc['auth']['qrGrantContinue'];
  'auth.cancel_qr': Rpc['auth']['cancelQr'];
  'calendar.entries': Rpc['calendar']['entries'];
  'calendar.save_event': Rpc['calendar']['saveEvent'];
  'calendar.answer_event': Rpc['calendar']['answerEvent'];
  'calls.turn_server': Rpc['calls']['turnServer'];
  'calls.rtc_transports': Rpc['calls']['rtcTransports'];
  'calls.rtc_livekit': Rpc['calls']['rtcLivekit'];
  'calls.join': Rpc['calls']['join'];
  'calls.compatibility': Rpc['calls']['compatibility'];
  'calls.support': Rpc['calls']['support'];
  'calls.leave': Rpc['calls']['leave'];
  'calls.set_screen_sharing': Rpc['calls']['setScreenSharing'];
  'calls.decline': Rpc['calls']['decline'];
  'encryption.status': Rpc['encryption']['status'];
  'encryption.key_backup_status': Rpc['encryption']['keyBackupStatus'];
  'encryption.download_key_backup': Rpc['encryption']['downloadKeyBackup'];
  'encryption.sign_out_safety': Rpc['encryption']['signOutSafety'];
  'encryption.devices': Rpc['encryption']['devices'];
  'encryption.user_security': Rpc['encryption']['userSecurity'];
  'encryption.recover_identity': Rpc['encryption']['recoverIdentity'];
  'encryption.enable_recovery': Rpc['encryption']['enableRecovery'];
  'encryption.reset_recovery_key': Rpc['encryption']['resetRecoveryKey'];
  'encryption.reset_identity': Rpc['encryption']['resetIdentity'];
  'encryption.continue_identity_reset': Rpc['encryption']['continueIdentityReset'];
  'encryption.cancel_identity_reset': Rpc['encryption']['cancelIdentityReset'];
  'encryption.export_room_keys': Rpc['encryption']['exportRoomKeys'];
  'encryption.import_room_keys': Rpc['encryption']['importRoomKeys'];
  'encryption.delete_device': Rpc['encryption']['deleteDevice'];
  'encryption.rename_device': Rpc['encryption']['renameDevice'];
  'encryption.discard_room_key': Rpc['encryption']['discardRoomKey'];
  'encryption.share_history': Rpc['encryption']['shareHistory'];
  'encryption.request_verification': Rpc['encryption']['requestVerification'];
  'encryption.withdraw_verification': Rpc['encryption']['withdrawVerification'];
  'encryption.set_device_blocked': Rpc['encryption']['setDeviceBlocked'];
  'encryption.accept_verification': Rpc['encryption']['acceptVerification'];
  'encryption.scan_verification_qr': Rpc['encryption']['scanVerificationQr'];
  'encryption.start_sas_verification': Rpc['encryption']['startSasVerification'];
  'encryption.confirm_verification': Rpc['encryption']['confirmVerification'];
  'encryption.cancel_verification': Rpc['encryption']['cancelVerification'];
  'members.list': Rpc['members']['list'];
  'members.permissions': Rpc['members']['permissions'];
  'members.power_levels': Rpc['members']['powerLevels'];
  'members.set_power_level': Rpc['members']['setPowerLevel'];
  'members.sync_power_levels_from_space': Rpc['members']['syncPowerLevelsFromSpace'];
  'members.sync_child_power_levels': Rpc['members']['syncChildPowerLevels'];
  'members.power_level_tags': Rpc['members']['powerLevelTags'];
  'members.set_power_level_tag': Rpc['members']['setPowerLevelTag'];
  'members.set_user_power_level': Rpc['members']['setUserPowerLevel'];
  'members.kick': Rpc['members']['kick'];
  'members.deny_knock': Rpc['members']['denyKnock'];
  'members.ban': Rpc['members']['ban'];
  'members.unban': Rpc['members']['unban'];
  'members.look': Rpc['members']['look'];
  'members.set_look': Rpc['members']['setLook'];
  'members.set_pronouns': Rpc['members']['setPronouns'];
  'messages.markdown_to_content': Rpc['messages']['markdownToContent'];
  'messages.send': Rpc['messages']['send'];
  'messages.send_raw_event': Rpc['messages']['sendRawEvent'];
  'messages.send_redaction': Rpc['messages']['sendRedaction'];
  'messages.send_sticker': Rpc['messages']['sendSticker'];
  'messages.send_gif': Rpc['messages']['sendGif'];
  'messages.remove_link_previews': Rpc['messages']['removeLinkPreviews'];
  'messages.reprofile': Rpc['messages']['reprofile'];
  'messages.edit': Rpc['messages']['edit'];
  'messages.redact': Rpc['messages']['redact'];
  'messages.delete_thread': Rpc['messages']['deleteThread'];
  'messages.bulk_redact': Rpc['messages']['bulkRedact'];
  'messages.react': Rpc['messages']['react'];
  'messages.send_location': Rpc['messages']['sendLocation'];
  'messages.forward': Rpc['messages']['forward'];
  'messages.report': Rpc['messages']['report'];
  'messages.retry_send': Rpc['messages']['retrySend'];
  'messages.cancel_send': Rpc['messages']['cancelSend'];
  'messages.schedule': Rpc['messages']['schedule'];
  'messages.schedule_attachment': Rpc['messages']['scheduleAttachment'];
  'messages.scheduled': Rpc['messages']['scheduled'];
  'messages.cancel_scheduled': Rpc['messages']['cancelScheduled'];
  'messages.send_scheduled': Rpc['messages']['sendScheduled'];
  'messages.create_poll': Rpc['messages']['createPoll'];
  'messages.vote_poll': Rpc['messages']['votePoll'];
  'messages.end_poll': Rpc['messages']['endPoll'];
  'messages.send_gesture': Rpc['messages']['sendGesture'];
  'messages.url_preview': Rpc['messages']['urlPreview'];
  'messages.reaction_shortcodes': Rpc['messages']['reactionShortcodes'];
  'messages.frequent_reactions': Rpc['messages']['frequentReactions'];
  'messages.set_typing': Rpc['messages']['setTyping'];
  'messages.media_config': Rpc['messages']['mediaConfig'];
  'notifications.settings': Rpc['notifications']['settings'];
  'notifications.default_modes': Rpc['notifications']['defaultModes'];
  'notifications.mention_notifications': Rpc['notifications']['mentionNotifications'];
  'notifications.event_notifications': Rpc['notifications']['eventNotifications'];
  'notifications.master_mute': Rpc['notifications']['masterMute'];
  'notifications.notification': Rpc['notifications']['notification'];
  'notifications.push_event': Rpc['notifications']['pushEvent'];
  'notifications.keywords': Rpc['notifications']['keywords'];
  'notifications.add_keyword': Rpc['notifications']['addKeyword'];
  'notifications.remove_keyword': Rpc['notifications']['removeKeyword'];
  'notifications.set_keyword_mode': Rpc['notifications']['setKeywordMode'];
  'notifications.set_room_notification_mode': Rpc['notifications']['setRoomNotificationMode'];
  'notifications.set_default_mode': Rpc['notifications']['setDefaultMode'];
  'notifications.set_mention_notifications': Rpc['notifications']['setMentionNotifications'];
  'notifications.set_event_notification': Rpc['notifications']['setEventNotification'];
  'notifications.set_master_mute': Rpc['notifications']['setMasterMute'];
  'notifications.inbox': Rpc['notifications']['inbox'];
  'notifications.backfill_inbox': Rpc['notifications']['backfillInbox'];
  'notifications.set_content': Rpc['notifications']['setContent'];
  'notifications.set_sounds': Rpc['notifications']['setSounds'];
  'notifications.set_notify_once': Rpc['notifications']['setNotifyOnce'];
  'notifications.set_enabled': Rpc['notifications']['setEnabled'];
  'notifications.push_rules': Rpc['notifications']['pushRules'];
  'notifications.set_push_rule_enabled': Rpc['notifications']['setPushRuleEnabled'];
  'notifications.set_push_rule_actions': Rpc['notifications']['setPushRuleActions'];
  'notifications.delete_push_rule': Rpc['notifications']['deletePushRule'];
  'packs.list': Rpc['packs']['list'];
  'packs.list_all': Rpc['packs']['listAll'];
  'packs.save': Rpc['packs']['save'];
  'packs.create_room_pack': Rpc['packs']['createRoomPack'];
  'packs.delete_room_pack': Rpc['packs']['deleteRoomPack'];
  'packs.convert_legacy': Rpc['packs']['convertLegacy'];
  'packs.remove_legacy': Rpc['packs']['removeLegacy'];
  'packs.add_account_images': Rpc['packs']['addAccountImages'];
  'packs.subscriptions': Rpc['packs']['subscriptions'];
  'packs.set_subscriptions': Rpc['packs']['setSubscriptions'];
  'personas.list': Rpc['personas']['list'];
  'personas.save': Rpc['personas']['save'];
  'personas.remove': Rpc['personas']['remove'];
  'personas.reorder': Rpc['personas']['reorder'];
  'personas.set_selection': Rpc['personas']['setSelection'];
  'personas.disable_room': Rpc['personas']['disableRoom'];
  'personas.backup': Rpc['personas']['backup'];
  'personas.check_backup': Rpc['personas']['checkBackup'];
  'personas.restore_backup': Rpc['personas']['restoreBackup'];
  'push.set_pusher': Rpc['push']['setPusher'];
  'push.remove_pusher': Rpc['push']['removePusher'];
  'push.web_pusher_support': Rpc['push']['webPusherSupport'];
  'push.set_web_pusher': Rpc['push']['setWebPusher'];
  'push.web_pushers': Rpc['push']['webPushers'];
  'push.ping_gateway': Rpc['push']['pingGateway'];
  'push.discover_gateway': Rpc['push']['discoverGateway'];
  'push.send_diagnostic': Rpc['push']['sendDiagnostic'];
  'push.ack_web_pusher': Rpc['push']['ackWebPusher'];
  'room_state.set_name': Rpc['room_state']['setName'];
  'room_state.set_topic': Rpc['room_state']['setTopic'];
  'room_state.set_avatar': Rpc['room_state']['setAvatar'];
  'room_state.set_join_rule': Rpc['room_state']['setJoinRule'];
  'room_state.history_visibility': Rpc['room_state']['historyVisibility'];
  'room_state.set_history_visibility': Rpc['room_state']['setHistoryVisibility'];
  'room_state.enable_encryption': Rpc['room_state']['enableEncryption'];
  'room_state.banner': Rpc['room_state']['banner'];
  'room_state.set_banner': Rpc['room_state']['setBanner'];
  'room_state.member_list_threshold': Rpc['room_state']['memberListThreshold'];
  'room_state.set_member_list_threshold': Rpc['room_state']['setMemberListThreshold'];
  'room_state.update_server_acl': Rpc['room_state']['updateServerAcl'];
  'room_state.abbreviations': Rpc['room_state']['abbreviations'];
  'room_state.set_abbreviations': Rpc['room_state']['setAbbreviations'];
  'room_state.event': Rpc['room_state']['event'];
  'room_state.events': Rpc['room_state']['events'];
  'room_state.events_raw': Rpc['room_state']['eventsRaw'];
  'room_state.full_state': Rpc['room_state']['fullState'];
  'room_state.send_event': Rpc['room_state']['sendEvent'];
  'room_state.account_data': Rpc['room_state']['accountData'];
  'room_state.account_data_types': Rpc['room_state']['accountDataTypes'];
  'room_state.set_account_data': Rpc['room_state']['setAccountData'];
  'room_state.account_data_raw': Rpc['room_state']['accountDataRaw'];
  'room_state.add_widget': Rpc['room_state']['addWidget'];
  'room_state.remove_widget': Rpc['room_state']['removeWidget'];
  'room_state.bot_commands': Rpc['room_state']['botCommands'];
  'rooms.open': Rpc['rooms']['open'];
  'rooms.list_item': Rpc['rooms']['listItem'];
  'rooms.preview': Rpc['rooms']['preview'];
  'rooms.join': Rpc['rooms']['join'];
  'rooms.knock': Rpc['rooms']['knock'];
  'rooms.via_servers': Rpc['rooms']['viaServers'];
  'rooms.leave': Rpc['rooms']['leave'];
  'rooms.invite': Rpc['rooms']['invite'];
  'rooms.create': Rpc['rooms']['create'];
  'rooms.create_dm': Rpc['rooms']['createDm'];
  'rooms.versions': Rpc['rooms']['versions'];
  'rooms.upgrade': Rpc['rooms']['upgrade'];
  'rooms.public_rooms': Rpc['rooms']['publicRooms'];
  'rooms.directory_visibility': Rpc['rooms']['directoryVisibility'];
  'rooms.set_directory_visibility': Rpc['rooms']['setDirectoryVisibility'];
  'rooms.aliases': Rpc['rooms']['aliases'];
  'rooms.create_alias': Rpc['rooms']['createAlias'];
  'rooms.delete_alias': Rpc['rooms']['deleteAlias'];
  'rooms.canonical_alias': Rpc['rooms']['canonicalAlias'];
  'rooms.set_canonical_alias': Rpc['rooms']['setCanonicalAlias'];
  'rooms.replaced_rooms': Rpc['rooms']['replacedRooms'];
  'rooms.set_tombstone': Rpc['rooms']['setTombstone'];
  'rooms.report': Rpc['rooms']['report'];
  'rooms.set_tag': Rpc['rooms']['setTag'];
  'rooms.attachments': Rpc['rooms']['attachments'];
  'rooms.cosmetics': Rpc['rooms']['cosmetics'];
  'search.messages': Rpc['search']['messages'];
  'search.coverage': Rpc['search']['coverage'];
  'search.metrics': Rpc['search']['metrics'];
  'session.restore': Rpc['session']['restore'];
  'session.list_accounts': Rpc['session']['listAccounts'];
  'session.switch_account': Rpc['session']['switchAccount'];
  'session.remove_account': Rpc['session']['removeAccount'];
  'session.logout': Rpc['session']['logout'];
  'session.reset_local_cache': Rpc['session']['resetLocalCache'];
  'session.homeserver_info': Rpc['session']['homeserverInfo'];
  'session.oauth_metadata': Rpc['session']['oauthMetadata'];
  'session.request_open_id_token': Rpc['session']['requestOpenIdToken'];
  'session.access_token': Rpc['session']['accessToken'];
  'session.open_id_token': Rpc['session']['openIdToken'];
  'session.sync_status': Rpc['session']['syncStatus'];
  'session.server_features': Rpc['session']['serverFeatures'];
  'session.delayed_events_supported': Rpc['session']['delayedEventsSupported'];
  'session.set_search_options': Rpc['session']['setSearchOptions'];
  'session.set_read_room': Rpc['session']['setReadRoom'];
  'session.integration_manager_url': Rpc['session']['integrationManagerUrl'];
  'session.integration_manager_terms': Rpc['session']['integrationManagerTerms'];
  'session.accept_integration_manager_terms': Rpc['session']['acceptIntegrationManagerTerms'];
  'session.known_rooms': Rpc['session']['knownRooms'];
  'spaces.add': Rpc['spaces']['add'];
  'spaces.set_child_order': Rpc['spaces']['setChildOrder'];
  'spaces.set_child_suggested': Rpc['spaces']['setChildSuggested'];
  'spaces.hierarchy': Rpc['spaces']['hierarchy'];
  'spaces.remove': Rpc['spaces']['remove'];
  'spaces.sidebar': Rpc['spaces']['sidebar'];
  'spaces.set_sidebar': Rpc['spaces']['setSidebar'];
  'spaces.room_has_space_parent': Rpc['spaces']['roomHasSpaceParent'];
  'spaces.unjoined_space_parents': Rpc['spaces']['unjoinedSpaceParents'];
  'timeline.subscribe_room_list': Rpc['timeline']['subscribeRoomList'];
  'timeline.subscribe': Rpc['timeline']['subscribe'];
  'timeline.unsubscribe': Rpc['timeline']['unsubscribe'];
  'timeline.warm_rooms': Rpc['timeline']['warmRooms'];
  'timeline.paginate': Rpc['timeline']['paginate'];
  'timeline.fetch_event_details': Rpc['timeline']['fetchEventDetails'];
  'timeline.event_items': Rpc['timeline']['eventItems'];
  'timeline.events': Rpc['timeline']['events'];
  'timeline.timestamp_to_event': Rpc['timeline']['timestampToEvent'];
  'timeline.list_threads': Rpc['timeline']['listThreads'];
  'timeline.event_source': Rpc['timeline']['eventSource'];
  'timeline.edit_history': Rpc['timeline']['editHistory'];
  'timeline.mark_read': Rpc['timeline']['markRead'];
  'timeline.set_fully_read': Rpc['timeline']['setFullyRead'];
  'timeline.mark_unread': Rpc['timeline']['markUnread'];
  'timeline.read_marker': Rpc['timeline']['readMarker'];
  'timeline.unread_diagnostics': Rpc['timeline']['unreadDiagnostics'];
  'timeline.retry_decryption': Rpc['timeline']['retryDecryption'];
  'timeline.room_event_relations': Rpc['timeline']['roomEventRelations'];
  'timeline.pinned_events': Rpc['timeline']['pinnedEvents'];
  'timeline.set_pinned': Rpc['timeline']['setPinned'];
  'timeline.pin_read_state': Rpc['timeline']['pinReadState'];
  'timeline.mark_pins_seen': Rpc['timeline']['markPinsSeen'];
  'widgets.send_delayed_event': Rpc['widgets']['sendDelayedEvent'];
  'widgets.send_sticky_event': Rpc['widgets']['sendStickyEvent'];
  'widgets.restart_delayed_event': Rpc['widgets']['restartDelayedEvent'];
  'widgets.send_to_device': Rpc['widgets']['sendToDevice'];
  'widgets.room_sticky_events': Rpc['widgets']['roomStickyEvents'];
  'widgets.set_widget_feed': Rpc['widgets']['setWidgetFeed'];
};

export type RpcCall = (method: keyof RpcMethods, params: unknown) => Promise<unknown>;

/** Binds every method to `call` on its wire name. */
export function createRpc(call: RpcCall): Rpc {
  return {
    account: {
      userProfile: (params) => call('account.user_profile', params) as ReturnType<Rpc['account']['userProfile']>,
      userRelations: (params) => call('account.user_relations', params) as ReturnType<Rpc['account']['userRelations']>,
      dataTypes: (params = {}) => call('account.data_types', params) as ReturnType<Rpc['account']['dataTypes']>,
      data: (params) => call('account.data', params) as ReturnType<Rpc['account']['data']>,
      setData: (params) => call('account.set_data', params) as ReturnType<Rpc['account']['setData']>,
      sealedData: (params) => call('account.sealed_data', params) as ReturnType<Rpc['account']['sealedData']>,
      setSealedData: (params) => call('account.set_sealed_data', params) as ReturnType<Rpc['account']['setSealedData']>,
      setDisplayName: (params) => call('account.set_display_name', params) as ReturnType<Rpc['account']['setDisplayName']>,
      setAvatarUrl: (params) => call('account.set_avatar_url', params) as ReturnType<Rpc['account']['setAvatarUrl']>,
      setProfileField: (params) => call('account.set_profile_field', params) as ReturnType<Rpc['account']['setProfileField']>,
      contacts: (params = {}) => call('account.contacts', params) as ReturnType<Rpc['account']['contacts']>,
      ignoredUsers: (params = {}) => call('account.ignored_users', params) as ReturnType<Rpc['account']['ignoredUsers']>,
      inviteTriage: (params = {}) => call('account.invite_triage', params) as ReturnType<Rpc['account']['inviteTriage']>,
      setDirect: (params) => call('account.set_direct', params) as ReturnType<Rpc['account']['setDirect']>,
      ignoreUser: (params) => call('account.ignore_user', params) as ReturnType<Rpc['account']['ignoreUser']>,
      unignoreUser: (params) => call('account.unignore_user', params) as ReturnType<Rpc['account']['unignoreUser']>,
      reportUser: (params) => call('account.report_user', params) as ReturnType<Rpc['account']['reportUser']>,
      deactivateAccount: (params) => call('account.deactivate_account', params) as ReturnType<Rpc['account']['deactivateAccount']>,
      setPresence: (params) => call('account.set_presence', params) as ReturnType<Rpc['account']['setPresence']>,
      setDeviceActivity: (params) => call('account.set_device_activity', params) as ReturnType<Rpc['account']['setDeviceActivity']>,
      fetchPresence: (params) => call('account.fetch_presence', params) as ReturnType<Rpc['account']['fetchPresence']>,
      searchUserDirectory: (params) => call('account.search_user_directory', params) as ReturnType<Rpc['account']['searchUserDirectory']>,
      dismissedInvites: (params = {}) => call('account.dismissed_invites', params) as ReturnType<Rpc['account']['dismissedInvites']>,
      setInviteDismissed: (params) => call('account.set_invite_dismissed', params) as ReturnType<Rpc['account']['setInviteDismissed']>,
      bookmarks: (params = {}) => call('account.bookmarks', params) as ReturnType<Rpc['account']['bookmarks']>,
      setBookmark: (params) => call('account.set_bookmark', params) as ReturnType<Rpc['account']['setBookmark']>,
      mediaPreviewConfig: (params) => call('account.media_preview_config', params) as ReturnType<Rpc['account']['mediaPreviewConfig']>,
      setMediaPreviewConfig: (params) => call('account.set_media_preview_config', params) as ReturnType<Rpc['account']['setMediaPreviewConfig']>,
      setRoomMediaPreviews: (params) => call('account.set_room_media_previews', params) as ReturnType<Rpc['account']['setRoomMediaPreviews']>,
      dmSpaces: (params = {}) => call('account.dm_spaces', params) as ReturnType<Rpc['account']['dmSpaces']>,
      setDmSpaces: (params) => call('account.set_dm_spaces', params) as ReturnType<Rpc['account']['setDmSpaces']>,
      profileOverrides: (params = {}) => call('account.profile_overrides', params) as ReturnType<Rpc['account']['profileOverrides']>,
      setProfileOverride: (params) => call('account.set_profile_override', params) as ReturnType<Rpc['account']['setProfileOverride']>,
    },
    auth: {
      discoverHomeserver: (params) => call('auth.discover_homeserver', params) as ReturnType<Rpc['auth']['discoverHomeserver']>,
      login: (params) => call('auth.login', params) as ReturnType<Rpc['auth']['login']>,
      loginFlows: (params) => call('auth.login_flows', params) as ReturnType<Rpc['auth']['loginFlows']>,
      registrationFlows: (params) => call('auth.registration_flows', params) as ReturnType<Rpc['auth']['registrationFlows']>,
      register: (params) => call('auth.register', params) as ReturnType<Rpc['auth']['register']>,
      requestRegistrationEmail: (params) => call('auth.request_registration_email', params) as ReturnType<Rpc['auth']['requestRegistrationEmail']>,
      submitRegistrationEmail: (params) => call('auth.submit_registration_email', params) as ReturnType<Rpc['auth']['submitRegistrationEmail']>,
      continueRegistration: (params = {}) => call('auth.continue_registration', params) as ReturnType<Rpc['auth']['continueRegistration']>,
      cancelRegistration: (params = {}) => call('auth.cancel_registration', params) as ReturnType<Rpc['auth']['cancelRegistration']>,
      requestPasswordResetEmail: (params) => call('auth.request_password_reset_email', params) as ReturnType<Rpc['auth']['requestPasswordResetEmail']>,
      resetPassword: (params) => call('auth.reset_password', params) as ReturnType<Rpc['auth']['resetPassword']>,
      startOidcLogin: (params) => call('auth.start_oidc_login', params) as ReturnType<Rpc['auth']['startOidcLogin']>,
      completeOidcLogin: (params) => call('auth.complete_oidc_login', params) as ReturnType<Rpc['auth']['completeOidcLogin']>,
      startSsoLogin: (params) => call('auth.start_sso_login', params) as ReturnType<Rpc['auth']['startSsoLogin']>,
      completeSsoLogin: (params) => call('auth.complete_sso_login', params) as ReturnType<Rpc['auth']['completeSsoLogin']>,
      startQrLogin: (params) => call('auth.start_qr_login', params) as ReturnType<Rpc['auth']['startQrLogin']>,
      startQrGrant: (params) => call('auth.start_qr_grant', params) as ReturnType<Rpc['auth']['startQrGrant']>,
      qrCheckCode: (params) => call('auth.qr_check_code', params) as ReturnType<Rpc['auth']['qrCheckCode']>,
      qrGrantContinue: (params) => call('auth.qr_grant_continue', params) as ReturnType<Rpc['auth']['qrGrantContinue']>,
      cancelQr: (params = {}) => call('auth.cancel_qr', params) as ReturnType<Rpc['auth']['cancelQr']>,
    },
    calendar: {
      entries: (params) => call('calendar.entries', params) as ReturnType<Rpc['calendar']['entries']>,
      saveEvent: (params) => call('calendar.save_event', params) as ReturnType<Rpc['calendar']['saveEvent']>,
      answerEvent: (params) => call('calendar.answer_event', params) as ReturnType<Rpc['calendar']['answerEvent']>,
    },
    calls: {
      turnServer: (params = {}) => call('calls.turn_server', params) as ReturnType<Rpc['calls']['turnServer']>,
      rtcTransports: (params = {}) => call('calls.rtc_transports', params) as ReturnType<Rpc['calls']['rtcTransports']>,
      rtcLivekit: (params) => call('calls.rtc_livekit', params) as ReturnType<Rpc['calls']['rtcLivekit']>,
      join: (params) => call('calls.join', params) as ReturnType<Rpc['calls']['join']>,
      compatibility: (params = {}) => call('calls.compatibility', params) as ReturnType<Rpc['calls']['compatibility']>,
      support: (params) => call('calls.support', params) as ReturnType<Rpc['calls']['support']>,
      leave: (params) => call('calls.leave', params) as ReturnType<Rpc['calls']['leave']>,
      setScreenSharing: (params) => call('calls.set_screen_sharing', params) as ReturnType<Rpc['calls']['setScreenSharing']>,
      decline: (params) => call('calls.decline', params) as ReturnType<Rpc['calls']['decline']>,
    },
    encryption: {
      status: (params = {}) => call('encryption.status', params) as ReturnType<Rpc['encryption']['status']>,
      keyBackupStatus: (params = {}) => call('encryption.key_backup_status', params) as ReturnType<Rpc['encryption']['keyBackupStatus']>,
      downloadKeyBackup: (params) => call('encryption.download_key_backup', params) as ReturnType<Rpc['encryption']['downloadKeyBackup']>,
      signOutSafety: (params = {}) => call('encryption.sign_out_safety', params) as ReturnType<Rpc['encryption']['signOutSafety']>,
      devices: (params = {}) => call('encryption.devices', params) as ReturnType<Rpc['encryption']['devices']>,
      userSecurity: (params) => call('encryption.user_security', params) as ReturnType<Rpc['encryption']['userSecurity']>,
      recoverIdentity: (params) => call('encryption.recover_identity', params) as ReturnType<Rpc['encryption']['recoverIdentity']>,
      enableRecovery: (params) => call('encryption.enable_recovery', params) as ReturnType<Rpc['encryption']['enableRecovery']>,
      resetRecoveryKey: (params) => call('encryption.reset_recovery_key', params) as ReturnType<Rpc['encryption']['resetRecoveryKey']>,
      resetIdentity: (params = {}) => call('encryption.reset_identity', params) as ReturnType<Rpc['encryption']['resetIdentity']>,
      continueIdentityReset: (params) => call('encryption.continue_identity_reset', params) as ReturnType<Rpc['encryption']['continueIdentityReset']>,
      cancelIdentityReset: (params = {}) => call('encryption.cancel_identity_reset', params) as ReturnType<Rpc['encryption']['cancelIdentityReset']>,
      exportRoomKeys: (params) => call('encryption.export_room_keys', params) as ReturnType<Rpc['encryption']['exportRoomKeys']>,
      importRoomKeys: (params) => call('encryption.import_room_keys', params) as ReturnType<Rpc['encryption']['importRoomKeys']>,
      deleteDevice: (params) => call('encryption.delete_device', params) as ReturnType<Rpc['encryption']['deleteDevice']>,
      renameDevice: (params) => call('encryption.rename_device', params) as ReturnType<Rpc['encryption']['renameDevice']>,
      discardRoomKey: (params) => call('encryption.discard_room_key', params) as ReturnType<Rpc['encryption']['discardRoomKey']>,
      shareHistory: (params) => call('encryption.share_history', params) as ReturnType<Rpc['encryption']['shareHistory']>,
      requestVerification: (params) => call('encryption.request_verification', params) as ReturnType<Rpc['encryption']['requestVerification']>,
      withdrawVerification: (params) => call('encryption.withdraw_verification', params) as ReturnType<Rpc['encryption']['withdrawVerification']>,
      setDeviceBlocked: (params) => call('encryption.set_device_blocked', params) as ReturnType<Rpc['encryption']['setDeviceBlocked']>,
      acceptVerification: (params) => call('encryption.accept_verification', params) as ReturnType<Rpc['encryption']['acceptVerification']>,
      scanVerificationQr: (params) => call('encryption.scan_verification_qr', params) as ReturnType<Rpc['encryption']['scanVerificationQr']>,
      startSasVerification: (params) => call('encryption.start_sas_verification', params) as ReturnType<Rpc['encryption']['startSasVerification']>,
      confirmVerification: (params) => call('encryption.confirm_verification', params) as ReturnType<Rpc['encryption']['confirmVerification']>,
      cancelVerification: (params) => call('encryption.cancel_verification', params) as ReturnType<Rpc['encryption']['cancelVerification']>,
    },
    members: {
      list: (params) => call('members.list', params) as ReturnType<Rpc['members']['list']>,
      permissions: (params) => call('members.permissions', params) as ReturnType<Rpc['members']['permissions']>,
      powerLevels: (params) => call('members.power_levels', params) as ReturnType<Rpc['members']['powerLevels']>,
      setPowerLevel: (params) => call('members.set_power_level', params) as ReturnType<Rpc['members']['setPowerLevel']>,
      syncPowerLevelsFromSpace: (params) => call('members.sync_power_levels_from_space', params) as ReturnType<Rpc['members']['syncPowerLevelsFromSpace']>,
      syncChildPowerLevels: (params) => call('members.sync_child_power_levels', params) as ReturnType<Rpc['members']['syncChildPowerLevels']>,
      powerLevelTags: (params) => call('members.power_level_tags', params) as ReturnType<Rpc['members']['powerLevelTags']>,
      setPowerLevelTag: (params) => call('members.set_power_level_tag', params) as ReturnType<Rpc['members']['setPowerLevelTag']>,
      setUserPowerLevel: (params) => call('members.set_user_power_level', params) as ReturnType<Rpc['members']['setUserPowerLevel']>,
      kick: (params) => call('members.kick', params) as ReturnType<Rpc['members']['kick']>,
      denyKnock: (params) => call('members.deny_knock', params) as ReturnType<Rpc['members']['denyKnock']>,
      ban: (params) => call('members.ban', params) as ReturnType<Rpc['members']['ban']>,
      unban: (params) => call('members.unban', params) as ReturnType<Rpc['members']['unban']>,
      look: (params) => call('members.look', params) as ReturnType<Rpc['members']['look']>,
      setLook: (params) => call('members.set_look', params) as ReturnType<Rpc['members']['setLook']>,
      setPronouns: (params) => call('members.set_pronouns', params) as ReturnType<Rpc['members']['setPronouns']>,
    },
    messages: {
      markdownToContent: (params) => call('messages.markdown_to_content', params) as ReturnType<Rpc['messages']['markdownToContent']>,
      send: (params) => call('messages.send', params) as ReturnType<Rpc['messages']['send']>,
      sendRawEvent: (params) => call('messages.send_raw_event', params) as ReturnType<Rpc['messages']['sendRawEvent']>,
      sendRedaction: (params) => call('messages.send_redaction', params) as ReturnType<Rpc['messages']['sendRedaction']>,
      sendSticker: (params) => call('messages.send_sticker', params) as ReturnType<Rpc['messages']['sendSticker']>,
      sendGif: (params) => call('messages.send_gif', params) as ReturnType<Rpc['messages']['sendGif']>,
      removeLinkPreviews: (params) => call('messages.remove_link_previews', params) as ReturnType<Rpc['messages']['removeLinkPreviews']>,
      reprofile: (params) => call('messages.reprofile', params) as ReturnType<Rpc['messages']['reprofile']>,
      edit: (params) => call('messages.edit', params) as ReturnType<Rpc['messages']['edit']>,
      redact: (params) => call('messages.redact', params) as ReturnType<Rpc['messages']['redact']>,
      deleteThread: (params) => call('messages.delete_thread', params) as ReturnType<Rpc['messages']['deleteThread']>,
      bulkRedact: (params) => call('messages.bulk_redact', params) as ReturnType<Rpc['messages']['bulkRedact']>,
      react: (params) => call('messages.react', params) as ReturnType<Rpc['messages']['react']>,
      sendLocation: (params) => call('messages.send_location', params) as ReturnType<Rpc['messages']['sendLocation']>,
      forward: (params) => call('messages.forward', params) as ReturnType<Rpc['messages']['forward']>,
      report: (params) => call('messages.report', params) as ReturnType<Rpc['messages']['report']>,
      retrySend: (params) => call('messages.retry_send', params) as ReturnType<Rpc['messages']['retrySend']>,
      cancelSend: (params) => call('messages.cancel_send', params) as ReturnType<Rpc['messages']['cancelSend']>,
      schedule: (params) => call('messages.schedule', params) as ReturnType<Rpc['messages']['schedule']>,
      scheduleAttachment: (params) => call('messages.schedule_attachment', params) as ReturnType<Rpc['messages']['scheduleAttachment']>,
      scheduled: (params) => call('messages.scheduled', params) as ReturnType<Rpc['messages']['scheduled']>,
      cancelScheduled: (params) => call('messages.cancel_scheduled', params) as ReturnType<Rpc['messages']['cancelScheduled']>,
      sendScheduled: (params) => call('messages.send_scheduled', params) as ReturnType<Rpc['messages']['sendScheduled']>,
      createPoll: (params) => call('messages.create_poll', params) as ReturnType<Rpc['messages']['createPoll']>,
      votePoll: (params) => call('messages.vote_poll', params) as ReturnType<Rpc['messages']['votePoll']>,
      endPoll: (params) => call('messages.end_poll', params) as ReturnType<Rpc['messages']['endPoll']>,
      sendGesture: (params) => call('messages.send_gesture', params) as ReturnType<Rpc['messages']['sendGesture']>,
      urlPreview: (params) => call('messages.url_preview', params) as ReturnType<Rpc['messages']['urlPreview']>,
      reactionShortcodes: (params) => call('messages.reaction_shortcodes', params) as ReturnType<Rpc['messages']['reactionShortcodes']>,
      frequentReactions: (params) => call('messages.frequent_reactions', params) as ReturnType<Rpc['messages']['frequentReactions']>,
      setTyping: (params) => call('messages.set_typing', params) as ReturnType<Rpc['messages']['setTyping']>,
      mediaConfig: (params = {}) => call('messages.media_config', params) as ReturnType<Rpc['messages']['mediaConfig']>,
    },
    notifications: {
      settings: (params) => call('notifications.settings', params) as ReturnType<Rpc['notifications']['settings']>,
      defaultModes: (params = {}) => call('notifications.default_modes', params) as ReturnType<Rpc['notifications']['defaultModes']>,
      mentionNotifications: (params = {}) => call('notifications.mention_notifications', params) as ReturnType<Rpc['notifications']['mentionNotifications']>,
      eventNotifications: (params = {}) => call('notifications.event_notifications', params) as ReturnType<Rpc['notifications']['eventNotifications']>,
      masterMute: (params = {}) => call('notifications.master_mute', params) as ReturnType<Rpc['notifications']['masterMute']>,
      notification: (params) => call('notifications.notification', params) as ReturnType<Rpc['notifications']['notification']>,
      pushEvent: (params) => call('notifications.push_event', params) as ReturnType<Rpc['notifications']['pushEvent']>,
      keywords: (params = {}) => call('notifications.keywords', params) as ReturnType<Rpc['notifications']['keywords']>,
      addKeyword: (params) => call('notifications.add_keyword', params) as ReturnType<Rpc['notifications']['addKeyword']>,
      removeKeyword: (params) => call('notifications.remove_keyword', params) as ReturnType<Rpc['notifications']['removeKeyword']>,
      setKeywordMode: (params) => call('notifications.set_keyword_mode', params) as ReturnType<Rpc['notifications']['setKeywordMode']>,
      setRoomNotificationMode: (params) => call('notifications.set_room_notification_mode', params) as ReturnType<Rpc['notifications']['setRoomNotificationMode']>,
      setDefaultMode: (params) => call('notifications.set_default_mode', params) as ReturnType<Rpc['notifications']['setDefaultMode']>,
      setMentionNotifications: (params) => call('notifications.set_mention_notifications', params) as ReturnType<Rpc['notifications']['setMentionNotifications']>,
      setEventNotification: (params) => call('notifications.set_event_notification', params) as ReturnType<Rpc['notifications']['setEventNotification']>,
      setMasterMute: (params) => call('notifications.set_master_mute', params) as ReturnType<Rpc['notifications']['setMasterMute']>,
      inbox: (params) => call('notifications.inbox', params) as ReturnType<Rpc['notifications']['inbox']>,
      backfillInbox: (params) => call('notifications.backfill_inbox', params) as ReturnType<Rpc['notifications']['backfillInbox']>,
      setContent: (params) => call('notifications.set_content', params) as ReturnType<Rpc['notifications']['setContent']>,
      setSounds: (params) => call('notifications.set_sounds', params) as ReturnType<Rpc['notifications']['setSounds']>,
      setNotifyOnce: (params) => call('notifications.set_notify_once', params) as ReturnType<Rpc['notifications']['setNotifyOnce']>,
      setEnabled: (params) => call('notifications.set_enabled', params) as ReturnType<Rpc['notifications']['setEnabled']>,
      pushRules: (params = {}) => call('notifications.push_rules', params) as ReturnType<Rpc['notifications']['pushRules']>,
      setPushRuleEnabled: (params) => call('notifications.set_push_rule_enabled', params) as ReturnType<Rpc['notifications']['setPushRuleEnabled']>,
      setPushRuleActions: (params) => call('notifications.set_push_rule_actions', params) as ReturnType<Rpc['notifications']['setPushRuleActions']>,
      deletePushRule: (params) => call('notifications.delete_push_rule', params) as ReturnType<Rpc['notifications']['deletePushRule']>,
    },
    packs: {
      list: (params) => call('packs.list', params) as ReturnType<Rpc['packs']['list']>,
      listAll: (params = {}) => call('packs.list_all', params) as ReturnType<Rpc['packs']['listAll']>,
      save: (params) => call('packs.save', params) as ReturnType<Rpc['packs']['save']>,
      createRoomPack: (params) => call('packs.create_room_pack', params) as ReturnType<Rpc['packs']['createRoomPack']>,
      deleteRoomPack: (params) => call('packs.delete_room_pack', params) as ReturnType<Rpc['packs']['deleteRoomPack']>,
      convertLegacy: (params) => call('packs.convert_legacy', params) as ReturnType<Rpc['packs']['convertLegacy']>,
      removeLegacy: (params) => call('packs.remove_legacy', params) as ReturnType<Rpc['packs']['removeLegacy']>,
      addAccountImages: (params) => call('packs.add_account_images', params) as ReturnType<Rpc['packs']['addAccountImages']>,
      subscriptions: (params = {}) => call('packs.subscriptions', params) as ReturnType<Rpc['packs']['subscriptions']>,
      setSubscriptions: (params) => call('packs.set_subscriptions', params) as ReturnType<Rpc['packs']['setSubscriptions']>,
    },
    personas: {
      list: (params = {}) => call('personas.list', params) as ReturnType<Rpc['personas']['list']>,
      save: (params) => call('personas.save', params) as ReturnType<Rpc['personas']['save']>,
      remove: (params) => call('personas.remove', params) as ReturnType<Rpc['personas']['remove']>,
      reorder: (params) => call('personas.reorder', params) as ReturnType<Rpc['personas']['reorder']>,
      setSelection: (params) => call('personas.set_selection', params) as ReturnType<Rpc['personas']['setSelection']>,
      disableRoom: (params) => call('personas.disable_room', params) as ReturnType<Rpc['personas']['disableRoom']>,
      backup: (params = {}) => call('personas.backup', params) as ReturnType<Rpc['personas']['backup']>,
      checkBackup: (params) => call('personas.check_backup', params) as ReturnType<Rpc['personas']['checkBackup']>,
      restoreBackup: (params) => call('personas.restore_backup', params) as ReturnType<Rpc['personas']['restoreBackup']>,
    },
    push: {
      setPusher: (params) => call('push.set_pusher', params) as ReturnType<Rpc['push']['setPusher']>,
      removePusher: (params) => call('push.remove_pusher', params) as ReturnType<Rpc['push']['removePusher']>,
      webPusherSupport: (params = {}) => call('push.web_pusher_support', params) as ReturnType<Rpc['push']['webPusherSupport']>,
      setWebPusher: (params) => call('push.set_web_pusher', params) as ReturnType<Rpc['push']['setWebPusher']>,
      webPushers: (params = {}) => call('push.web_pushers', params) as ReturnType<Rpc['push']['webPushers']>,
      pingGateway: (params) => call('push.ping_gateway', params) as ReturnType<Rpc['push']['pingGateway']>,
      discoverGateway: (params) => call('push.discover_gateway', params) as ReturnType<Rpc['push']['discoverGateway']>,
      sendDiagnostic: (params) => call('push.send_diagnostic', params) as ReturnType<Rpc['push']['sendDiagnostic']>,
      ackWebPusher: (params) => call('push.ack_web_pusher', params) as ReturnType<Rpc['push']['ackWebPusher']>,
    },
    room_state: {
      setName: (params) => call('room_state.set_name', params) as ReturnType<Rpc['room_state']['setName']>,
      setTopic: (params) => call('room_state.set_topic', params) as ReturnType<Rpc['room_state']['setTopic']>,
      setAvatar: (params) => call('room_state.set_avatar', params) as ReturnType<Rpc['room_state']['setAvatar']>,
      setJoinRule: (params) => call('room_state.set_join_rule', params) as ReturnType<Rpc['room_state']['setJoinRule']>,
      historyVisibility: (params) => call('room_state.history_visibility', params) as ReturnType<Rpc['room_state']['historyVisibility']>,
      setHistoryVisibility: (params) => call('room_state.set_history_visibility', params) as ReturnType<Rpc['room_state']['setHistoryVisibility']>,
      enableEncryption: (params) => call('room_state.enable_encryption', params) as ReturnType<Rpc['room_state']['enableEncryption']>,
      banner: (params) => call('room_state.banner', params) as ReturnType<Rpc['room_state']['banner']>,
      setBanner: (params) => call('room_state.set_banner', params) as ReturnType<Rpc['room_state']['setBanner']>,
      memberListThreshold: (params) => call('room_state.member_list_threshold', params) as ReturnType<Rpc['room_state']['memberListThreshold']>,
      setMemberListThreshold: (params) => call('room_state.set_member_list_threshold', params) as ReturnType<Rpc['room_state']['setMemberListThreshold']>,
      updateServerAcl: (params) => call('room_state.update_server_acl', params) as ReturnType<Rpc['room_state']['updateServerAcl']>,
      abbreviations: (params) => call('room_state.abbreviations', params) as ReturnType<Rpc['room_state']['abbreviations']>,
      setAbbreviations: (params) => call('room_state.set_abbreviations', params) as ReturnType<Rpc['room_state']['setAbbreviations']>,
      event: (params) => call('room_state.event', params) as ReturnType<Rpc['room_state']['event']>,
      events: (params) => call('room_state.events', params) as ReturnType<Rpc['room_state']['events']>,
      eventsRaw: (params) => call('room_state.events_raw', params) as ReturnType<Rpc['room_state']['eventsRaw']>,
      fullState: (params) => call('room_state.full_state', params) as ReturnType<Rpc['room_state']['fullState']>,
      sendEvent: (params) => call('room_state.send_event', params) as ReturnType<Rpc['room_state']['sendEvent']>,
      accountData: (params) => call('room_state.account_data', params) as ReturnType<Rpc['room_state']['accountData']>,
      accountDataTypes: (params) => call('room_state.account_data_types', params) as ReturnType<Rpc['room_state']['accountDataTypes']>,
      setAccountData: (params) => call('room_state.set_account_data', params) as ReturnType<Rpc['room_state']['setAccountData']>,
      accountDataRaw: (params) => call('room_state.account_data_raw', params) as ReturnType<Rpc['room_state']['accountDataRaw']>,
      addWidget: (params) => call('room_state.add_widget', params) as ReturnType<Rpc['room_state']['addWidget']>,
      removeWidget: (params) => call('room_state.remove_widget', params) as ReturnType<Rpc['room_state']['removeWidget']>,
      botCommands: (params) => call('room_state.bot_commands', params) as ReturnType<Rpc['room_state']['botCommands']>,
    },
    rooms: {
      open: (params) => call('rooms.open', params) as ReturnType<Rpc['rooms']['open']>,
      listItem: (params) => call('rooms.list_item', params) as ReturnType<Rpc['rooms']['listItem']>,
      preview: (params) => call('rooms.preview', params) as ReturnType<Rpc['rooms']['preview']>,
      join: (params) => call('rooms.join', params) as ReturnType<Rpc['rooms']['join']>,
      knock: (params) => call('rooms.knock', params) as ReturnType<Rpc['rooms']['knock']>,
      viaServers: (params) => call('rooms.via_servers', params) as ReturnType<Rpc['rooms']['viaServers']>,
      leave: (params) => call('rooms.leave', params) as ReturnType<Rpc['rooms']['leave']>,
      invite: (params) => call('rooms.invite', params) as ReturnType<Rpc['rooms']['invite']>,
      create: (params) => call('rooms.create', params) as ReturnType<Rpc['rooms']['create']>,
      createDm: (params) => call('rooms.create_dm', params) as ReturnType<Rpc['rooms']['createDm']>,
      versions: (params = {}) => call('rooms.versions', params) as ReturnType<Rpc['rooms']['versions']>,
      upgrade: (params) => call('rooms.upgrade', params) as ReturnType<Rpc['rooms']['upgrade']>,
      publicRooms: (params) => call('rooms.public_rooms', params) as ReturnType<Rpc['rooms']['publicRooms']>,
      directoryVisibility: (params) => call('rooms.directory_visibility', params) as ReturnType<Rpc['rooms']['directoryVisibility']>,
      setDirectoryVisibility: (params) => call('rooms.set_directory_visibility', params) as ReturnType<Rpc['rooms']['setDirectoryVisibility']>,
      aliases: (params) => call('rooms.aliases', params) as ReturnType<Rpc['rooms']['aliases']>,
      createAlias: (params) => call('rooms.create_alias', params) as ReturnType<Rpc['rooms']['createAlias']>,
      deleteAlias: (params) => call('rooms.delete_alias', params) as ReturnType<Rpc['rooms']['deleteAlias']>,
      canonicalAlias: (params) => call('rooms.canonical_alias', params) as ReturnType<Rpc['rooms']['canonicalAlias']>,
      setCanonicalAlias: (params) => call('rooms.set_canonical_alias', params) as ReturnType<Rpc['rooms']['setCanonicalAlias']>,
      replacedRooms: (params = {}) => call('rooms.replaced_rooms', params) as ReturnType<Rpc['rooms']['replacedRooms']>,
      setTombstone: (params) => call('rooms.set_tombstone', params) as ReturnType<Rpc['rooms']['setTombstone']>,
      report: (params) => call('rooms.report', params) as ReturnType<Rpc['rooms']['report']>,
      setTag: (params) => call('rooms.set_tag', params) as ReturnType<Rpc['rooms']['setTag']>,
      attachments: (params) => call('rooms.attachments', params) as ReturnType<Rpc['rooms']['attachments']>,
      cosmetics: (params) => call('rooms.cosmetics', params) as ReturnType<Rpc['rooms']['cosmetics']>,
    },
    search: {
      messages: (params) => call('search.messages', params) as ReturnType<Rpc['search']['messages']>,
      coverage: (params = {}) => call('search.coverage', params) as ReturnType<Rpc['search']['coverage']>,
      metrics: (params = {}) => call('search.metrics', params) as ReturnType<Rpc['search']['metrics']>,
    },
    session: {
      restore: (params = {}) => call('session.restore', params) as ReturnType<Rpc['session']['restore']>,
      listAccounts: (params = {}) => call('session.list_accounts', params) as ReturnType<Rpc['session']['listAccounts']>,
      switchAccount: (params) => call('session.switch_account', params) as ReturnType<Rpc['session']['switchAccount']>,
      removeAccount: (params) => call('session.remove_account', params) as ReturnType<Rpc['session']['removeAccount']>,
      logout: (params = {}) => call('session.logout', params) as ReturnType<Rpc['session']['logout']>,
      resetLocalCache: (params = {}) => call('session.reset_local_cache', params) as ReturnType<Rpc['session']['resetLocalCache']>,
      homeserverInfo: (params = {}) => call('session.homeserver_info', params) as ReturnType<Rpc['session']['homeserverInfo']>,
      oauthMetadata: (params = {}) => call('session.oauth_metadata', params) as ReturnType<Rpc['session']['oauthMetadata']>,
      requestOpenIdToken: (params = {}) => call('session.request_open_id_token', params) as ReturnType<Rpc['session']['requestOpenIdToken']>,
      accessToken: (params = {}) => call('session.access_token', params) as ReturnType<Rpc['session']['accessToken']>,
      openIdToken: (params = {}) => call('session.open_id_token', params) as ReturnType<Rpc['session']['openIdToken']>,
      syncStatus: (params = {}) => call('session.sync_status', params) as ReturnType<Rpc['session']['syncStatus']>,
      serverFeatures: (params = {}) => call('session.server_features', params) as ReturnType<Rpc['session']['serverFeatures']>,
      delayedEventsSupported: (params = {}) => call('session.delayed_events_supported', params) as ReturnType<Rpc['session']['delayedEventsSupported']>,
      setSearchOptions: (params) => call('session.set_search_options', params) as ReturnType<Rpc['session']['setSearchOptions']>,
      setReadRoom: (params) => call('session.set_read_room', params) as ReturnType<Rpc['session']['setReadRoom']>,
      integrationManagerUrl: (params) => call('session.integration_manager_url', params) as ReturnType<Rpc['session']['integrationManagerUrl']>,
      integrationManagerTerms: (params = {}) => call('session.integration_manager_terms', params) as ReturnType<Rpc['session']['integrationManagerTerms']>,
      acceptIntegrationManagerTerms: (params) => call('session.accept_integration_manager_terms', params) as ReturnType<Rpc['session']['acceptIntegrationManagerTerms']>,
      knownRooms: (params = {}) => call('session.known_rooms', params) as ReturnType<Rpc['session']['knownRooms']>,
    },
    spaces: {
      add: (params) => call('spaces.add', params) as ReturnType<Rpc['spaces']['add']>,
      setChildOrder: (params) => call('spaces.set_child_order', params) as ReturnType<Rpc['spaces']['setChildOrder']>,
      setChildSuggested: (params) => call('spaces.set_child_suggested', params) as ReturnType<Rpc['spaces']['setChildSuggested']>,
      hierarchy: (params) => call('spaces.hierarchy', params) as ReturnType<Rpc['spaces']['hierarchy']>,
      remove: (params) => call('spaces.remove', params) as ReturnType<Rpc['spaces']['remove']>,
      sidebar: (params = {}) => call('spaces.sidebar', params) as ReturnType<Rpc['spaces']['sidebar']>,
      setSidebar: (params) => call('spaces.set_sidebar', params) as ReturnType<Rpc['spaces']['setSidebar']>,
      roomHasSpaceParent: (params) => call('spaces.room_has_space_parent', params) as ReturnType<Rpc['spaces']['roomHasSpaceParent']>,
      unjoinedSpaceParents: (params) => call('spaces.unjoined_space_parents', params) as ReturnType<Rpc['spaces']['unjoinedSpaceParents']>,
    },
    timeline: {
      subscribeRoomList: (params = {}) => call('timeline.subscribe_room_list', params) as ReturnType<Rpc['timeline']['subscribeRoomList']>,
      subscribe: (params) => call('timeline.subscribe', params) as ReturnType<Rpc['timeline']['subscribe']>,
      unsubscribe: (params) => call('timeline.unsubscribe', params) as ReturnType<Rpc['timeline']['unsubscribe']>,
      warmRooms: (params) => call('timeline.warm_rooms', params) as ReturnType<Rpc['timeline']['warmRooms']>,
      paginate: (params) => call('timeline.paginate', params) as ReturnType<Rpc['timeline']['paginate']>,
      fetchEventDetails: (params) => call('timeline.fetch_event_details', params) as ReturnType<Rpc['timeline']['fetchEventDetails']>,
      eventItems: (params) => call('timeline.event_items', params) as ReturnType<Rpc['timeline']['eventItems']>,
      events: (params) => call('timeline.events', params) as ReturnType<Rpc['timeline']['events']>,
      timestampToEvent: (params) => call('timeline.timestamp_to_event', params) as ReturnType<Rpc['timeline']['timestampToEvent']>,
      listThreads: (params) => call('timeline.list_threads', params) as ReturnType<Rpc['timeline']['listThreads']>,
      eventSource: (params) => call('timeline.event_source', params) as ReturnType<Rpc['timeline']['eventSource']>,
      editHistory: (params) => call('timeline.edit_history', params) as ReturnType<Rpc['timeline']['editHistory']>,
      markRead: (params) => call('timeline.mark_read', params) as ReturnType<Rpc['timeline']['markRead']>,
      setFullyRead: (params) => call('timeline.set_fully_read', params) as ReturnType<Rpc['timeline']['setFullyRead']>,
      markUnread: (params) => call('timeline.mark_unread', params) as ReturnType<Rpc['timeline']['markUnread']>,
      readMarker: (params) => call('timeline.read_marker', params) as ReturnType<Rpc['timeline']['readMarker']>,
      unreadDiagnostics: (params) => call('timeline.unread_diagnostics', params) as ReturnType<Rpc['timeline']['unreadDiagnostics']>,
      retryDecryption: (params) => call('timeline.retry_decryption', params) as ReturnType<Rpc['timeline']['retryDecryption']>,
      roomEventRelations: (params) => call('timeline.room_event_relations', params) as ReturnType<Rpc['timeline']['roomEventRelations']>,
      pinnedEvents: (params) => call('timeline.pinned_events', params) as ReturnType<Rpc['timeline']['pinnedEvents']>,
      setPinned: (params) => call('timeline.set_pinned', params) as ReturnType<Rpc['timeline']['setPinned']>,
      pinReadState: (params) => call('timeline.pin_read_state', params) as ReturnType<Rpc['timeline']['pinReadState']>,
      markPinsSeen: (params) => call('timeline.mark_pins_seen', params) as ReturnType<Rpc['timeline']['markPinsSeen']>,
    },
    widgets: {
      sendDelayedEvent: (params) => call('widgets.send_delayed_event', params) as ReturnType<Rpc['widgets']['sendDelayedEvent']>,
      sendStickyEvent: (params) => call('widgets.send_sticky_event', params) as ReturnType<Rpc['widgets']['sendStickyEvent']>,
      restartDelayedEvent: (params) => call('widgets.restart_delayed_event', params) as ReturnType<Rpc['widgets']['restartDelayedEvent']>,
      sendToDevice: (params) => call('widgets.send_to_device', params) as ReturnType<Rpc['widgets']['sendToDevice']>,
      roomStickyEvents: (params) => call('widgets.room_sticky_events', params) as ReturnType<Rpc['widgets']['roomStickyEvents']>,
      setWidgetFeed: (params) => call('widgets.set_widget_feed', params) as ReturnType<Rpc['widgets']['setWidgetFeed']>,
    },
  };
}

